import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import {
  CoreUserGroups,
  Countries,
  InvoiceStatus,
  InvoiceType,
  isAdminOnlyRoute,
  isRouteOnStage,
  PLAN_MAX_TIER,
  requiredPlanForRoute,
  routeTier,
  Routes,
  Scopes
} from '@simplyanvil/cl-types';
import type { OrganisationPlan, Stage } from '@simplyanvil/cl-types';
import * as Yup from 'yup';
import { AnySchema } from 'yup';
import { SearchFiltersFormFields } from '@/components/Dashboard/SearchFiltersForm';
import { StringTMap } from './old-clTypes';
import sortBy from 'lodash-es/sortBy';
import uniqBy from 'lodash-es/uniqBy';
import round from 'lodash-es/round';
import { sanitiseCsvCell } from './csvSanitise';
import { calculateDocumentTotals, calculateLineTax } from '@simplyanvil/eezi-utils';

dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(isoWeek);
dayjs.extend(utc);

export const phoneRegExp = /^((\+\d{1,3})|0)[1-9][0-9]{7,}$/; // min length of 8char. // min length of 8char.
export const passwordRegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*\-_.])[\w\d!@#$%^&*\-_.]/;
export const nameRegExp = /^[^±!@£$%^&*_+§¡€#¢¶•ªº«\\/<>?:;|=.,0-9()]*$/;
export const fullPeppolIdRegExp = /^[a-zA-Z0-9\-]+::[a-zA-Z0-9\-]+:[a-zA-Z0-9_\-]+$/; // iso6523-actorid-upis::9915:test
export const shortPeppolIdRegExp = /^[a-zA-Z0-9\-]+:[a-zA-Z0-9_\-]+$/; // 9915:test
// Hungarian tax ID pattern (8 digits, optional -1- and 2-digit region code)
export const taxIdRegExpHUN = /^[0-9]{8}(-1-[0-9]{2})?$/;
// Hungarian VAT code pattern (HU + 8 digits)
export const vatCodeRegExpHUN = /^HU[0-9]{8}$/;

export const sanitizePhone = (phone: string): string => phone.replace(/^0/, '+27');

export const safeJsonParse = <T = any>(value: unknown): T | null => {
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

export const parseJsonOrUndefined = <T = any>(value: unknown): T | undefined => safeJsonParse<T>(value) ?? undefined;

export const formatDocumentDate = (value: unknown, format = 'YYYY-MM-DD'): string => {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = dayjs(value as string | number | Date);
  return parsed.isValid() ? parsed.format(format) : '—';
};

const isValidCurrencyCode = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z]{3}$/.test(value);

export const formatQuantity = (val: unknown): string => {
  const num = typeof val === 'number' ? val : Number(val);
  if (!Number.isFinite(num)) return '0.00';
  const [, decimal = ''] = num.toString().split('.');
  return decimal.length > 2 ? num.toFixed(4) : num.toFixed(2);
};

export const formatMoney = (amount: number | null | undefined, currency: unknown): string => {
  const value = Number.isFinite(amount as number) ? (amount as number) : 0;
  if (isValidCurrencyCode(currency)) {
    // Add exception for TWN to display TWD as NT$ instead of $
    if (['TWD'].includes(currency)) {
      return new Intl.NumberFormat('en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        currency,
        currencyDisplay: 'symbol',
        style: 'currency'
      }).format(value);
    }

    return new Intl.NumberFormat('en', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      currency,
      currencyDisplay: 'narrowSymbol',
      style: 'currency'
    }).format(value);
  }
  return new Intl.NumberFormat('en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export const removeNullValues = (obj) => {
  if (Array.isArray(obj)) {
    return obj.filter((value) => value !== null).map(removeNullValues);
  }
  if (typeof obj === 'object' && obj !== null) {
    const newObj = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== null) {
        newObj[key] = removeNullValues(value);
      }
    });
    return newObj;
  }
  return obj;
};

/**
 * Symmetric rounding that handles negative numbers correctly.
 * Math.round(-4740.5) = -4740, but we want -4741 to match how positives round.
 * This ensures: symmetricRound(-x) === -symmetricRound(x)
 *
 * @param value - The number to round
 * @param decimals - Number of decimal places (default: 0)
 */
export const symmetricRound = (value: number, decimals = 2): number =>
  Math.sign(value) * round(Math.abs(value), decimals);

// Extract the hex value of the referenced chakra color variable.
export const getComputedCSSVariableValue = (variable: string): string =>
  getComputedStyle(document.documentElement)
    .getPropertyValue(variable.slice(4, variable.length - 1))
    .trim();

const TEST_LABEL_OVERRIDES: Record<string, string> = {
  DETECNO_MEX_MOCK: 'Mexico v2 (Test)',
  DEU_XRECHNUNG: 'Germany - XRechnung (Test)',
  DEU_ZUGFERD: 'Germany - ZUGFeRD (Test)',
  MYS_PEPPOL: 'Malaysia - Peppol (Test)',
  MYS_TA: 'Malaysia - Tax Authority (Test)',
  DNK_NEMHANDEL: 'Denmark - NemHandel (Test)',
  DNK_PEPPOL: 'Denmark - Peppol (Test)',
  ARE_TA: 'United Arab Emirates - Tax Authority (Test)',
  ARE_PEPPOL: 'United Arab Emirates - Peppol (Test)',
  ITA: 'Italy - Legacy (Test)',
  PRT: 'Portugal - Legacy (Test)',
  ROU: 'Romania - Legacy (Test)'
};
const LIVE_ONLY_ROUTE_VALUES = new Set(['BIRCHSTREET']);
// Routes that never appear in the public picker: only holders of the scope (and admins) see them.
const INTERNAL_ROUTE_SCOPES = new Set<string>([Scopes.ORG_FRANCE_OUTPOST]);
type ReactSelectOption = { label: string; value: string };
export type ReactSelectGroup = {
  value: string;
  label: string;
  group?: string;
  locked?: true;
  requiredPlan?: 'Plus' | 'Enterprise';
};

export interface RouteSelectOptionsOpts {
  plan?: string | null;
  ownCountry?: string | null;
  stage?: Stage;
}

export const buildRouteSelectOptions = (
  userAccess: {
    scopes?: Scopes[];
    groups?: CoreUserGroups[];
  },
  currentRoute: string = null,
  hasPaidPlan: boolean = true,
  excludeLiveOnlyMocks: boolean = false,
  opts: RouteSelectOptionsOpts = {}
): ReactSelectGroup[] => {
  const isAdminOrDeveloper =
    !!userAccess?.groups?.includes(CoreUserGroups.ADMIN) || !!userAccess?.groups?.includes(CoreUserGroups.DEVELOPER);
  // Tier gating (opts.stage set): the hand-written scope blocks below are the public route list and
  // its naming, so every public route scope is granted here; stage and plan (not scopes) decide what
  // is shown or locked. Internal scopes stay with their holders and admins.
  const grantedScopes: Scopes[] = opts.stage
    ? [
        ...(Object.values(Scopes) as Scopes[]).filter(
          (scope) => !INTERNAL_ROUTE_SCOPES.has(scope) || isAdminOrDeveloper || userAccess?.scopes?.includes(scope)
        ),
        ...(userAccess?.scopes ?? [])
      ]
    : userAccess.scopes;
  const access = currentRoute ? [...grantedScopes, `org:${currentRoute.toLowerCase()}`] : grantedScopes;
  const networkRoutes: ReactSelectOption[] = [];
  const connectorRoutes: ReactSelectOption[] = [];
  const utilityRoutes: ReactSelectOption[] = [];
  if (access.includes(Scopes.ORG_PEPPOL)) {
    networkRoutes.push({ label: 'Peppol', value: 'PEPPOL' });
  }
  if (access.includes(Scopes.ORG_APOC)) {
    connectorRoutes.push({ label: 'APOC', value: 'APOC' });
  }
  if (access.includes(Scopes.ORG_BIRCHSTREET)) {
    connectorRoutes.push({ label: 'BirchStreet', value: 'BIRCHSTREET' });
  }

  // Legacy (ungated) callers — admin filters and the follow-up-route selects — keep the historical
  // India/Japan/Mexico seed so existing follow-up values still render. Gated mode gets them via scopes.
  const countryRoutes: ReactSelectOption[] = opts.stage
    ? []
    : [
        { label: 'India', value: 'IND' },
        { label: 'Japan', value: 'JPN' },
        { label: 'Mexico', value: 'MEX_SCI' }
      ];

  const currentCountryRoute: ReactSelectOption[] = [];

  if (access.includes(Scopes.ORG_ANGOLA)) {
    countryRoutes.push({ label: 'Angola', value: 'AGO' });
    currentCountryRoute.push({ label: 'Angola', value: 'AGO' });
  }

  if (access.includes(Scopes.ORG_AUSTRALIA)) {
    countryRoutes.push({ label: 'Australia', value: 'AUS' });
    currentCountryRoute.push({ label: 'Australia', value: 'AUS' });
  }

  if (access.includes(Scopes.ORG_BELGIUM)) {
    countryRoutes.push({ label: 'Belgium', value: 'BEL' });
    currentCountryRoute.push({ label: 'Belgium', value: 'BEL' });
  }

  if (access.includes(Scopes.ORG_CHINA)) {
    countryRoutes.push({ label: 'China', value: 'CHN' });
    currentCountryRoute.push({ label: 'China', value: 'CHN' });
  }

  if (access.includes(Scopes.ORG_CROATIA)) {
    countryRoutes.push({ label: 'Croatia', value: 'HRV' });
    currentCountryRoute.push({ label: 'Croatia', value: 'HRV' });
  }

  if (access.includes(Scopes.ORG_DENMARK)) {
    countryRoutes.push({ label: 'Denmark - NemHandel', value: 'DNK_NEMHANDEL' });
    currentCountryRoute.push({ label: 'Denmark - NemHandel', value: 'DNK_NEMHANDEL' });
  }

  if (access.includes(Scopes.ORG_EGYPT)) {
    countryRoutes.push({ label: 'Egypt', value: 'EGY' });
    currentCountryRoute.push({ label: 'Egypt', value: 'EGY' });
  }

  if (access.includes(Scopes.ORG_FRANCE)) {
    countryRoutes.push({ label: 'France', value: 'FRA' });
    currentCountryRoute.push({ label: 'France', value: 'FRA' });

    countryRoutes.push({ label: 'France - e-Invoicing', value: 'FRA_EINVOICE' });
    currentCountryRoute.push({ label: 'France - e-Invoicing', value: 'FRA_EINVOICE' });
  }
  if (access.includes(Scopes.ORG_FRANCE_OUTPOST)) {
    countryRoutes.push({ label: 'France Outpost', value: 'FRA_OUTPOST' });
    currentCountryRoute.push({ label: 'France Outpost', value: 'FRA_OUTPOST' });
  }
  if (access.includes(Scopes.ORG_FRANCE_EREPORT)) {
    countryRoutes.push({ label: 'France - e-Reporting', value: 'FRA_EREPORT' });
    currentCountryRoute.push({ label: 'France - e-Reporting', value: 'FRA_EREPORT' });
  }

  if (access.includes(Scopes.ORG_GERMANY)) {
    countryRoutes.push({ label: 'Germany', value: 'DEU' });
    currentCountryRoute.push({ label: 'Germany', value: 'DEU' });

    countryRoutes.push({ label: 'Germany - XRechnung', value: 'DEU_XRECHNUNG' });
    currentCountryRoute.push({ label: 'Germany - XRechnung', value: 'DEU_XRECHNUNG' });

    countryRoutes.push({ label: 'Germany - ZUGFeRD', value: 'DEU_ZUGFERD' });
    currentCountryRoute.push({ label: 'Germany - ZUGFeRD', value: 'DEU_ZUGFERD' });
  }

  if (access.includes(Scopes.ORG_GREECE)) {
    countryRoutes.push({ label: 'Greece', value: 'GRC' });
    currentCountryRoute.push({ label: 'Greece', value: 'GRC' });
  }

  if (access.includes(Scopes.ORG_GHANA)) {
    countryRoutes.push({ label: 'Ghana', value: 'GHA' });
    currentCountryRoute.push({ label: 'Ghana', value: 'GHA' });
  }

  if (access.includes(Scopes.ORG_HUNGARY)) {
    countryRoutes.push({ label: 'Hungary', value: 'HUN' });
    currentCountryRoute.push({ label: 'Hungary', value: 'HUN' });
  }

  if (access.includes(Scopes.ORG_INDIA)) {
    countryRoutes.push({ label: 'India', value: 'IND' });
    currentCountryRoute.push({ label: 'India', value: 'IND' });
  }

  if (access.includes(Scopes.ORG_INDONESIA)) {
    countryRoutes.push({ label: 'Indonesia', value: 'IDN' });
    currentCountryRoute.push({ label: 'Indonesia', value: 'IDN' });
  }

  if (access.includes(Scopes.ORG_ITALY)) {
    countryRoutes.push({ label: 'Italy (Legacy)', value: 'ITA' });
    currentCountryRoute.push({ label: 'Italy (Legacy)', value: 'ITA' });
  }

  if (access.includes(Scopes.ORG_ITALY_DIRECT)) {
    countryRoutes.push({ label: 'Italy', value: 'ITA_DIRECT' });
    currentCountryRoute.push({ label: 'Italy', value: 'ITA_DIRECT' });
  }

  if (access.includes(Scopes.ORG_JAPAN)) {
    countryRoutes.push({ label: 'Japan', value: 'JPN' });
    currentCountryRoute.push({ label: 'Japan', value: 'JPN' });
  }

  if (access.includes(Scopes.ORG_JORDAN)) {
    countryRoutes.push({ label: 'Jordan', value: 'JOR' });
    currentCountryRoute.push({ label: 'Jordan', value: 'JOR' });
  }

  if (access.includes(Scopes.ORG_KENYA)) {
    countryRoutes.push({ label: 'Kenya', value: 'KEN' });
    currentCountryRoute.push({ label: 'Kenya', value: 'KEN' });
  }

  if (access.includes(Scopes.ORG_MALAYSIA)) {
    countryRoutes.push({ label: 'Malaysia', value: 'MYS' });
    currentCountryRoute.push({ label: 'Malaysia', value: 'MYS' });

    countryRoutes.push({ label: 'Malaysia - Peppol', value: 'MYS_PEPPOL' });
    currentCountryRoute.push({ label: 'Malaysia - Peppol', value: 'MYS_PEPPOL' });

    countryRoutes.push({ label: 'Malaysia - Tax Authority', value: 'MYS_TA' });
    currentCountryRoute.push({ label: 'Malaysia - Tax Authority', value: 'MYS_TA' });
  }

  if (access.includes(Scopes.ORG_MAURITIUS)) {
    countryRoutes.push({ label: 'Mauritius', value: 'MUS' });
    currentCountryRoute.push({ label: 'Mauritius', value: 'MUS' });
  }

  if (access.includes(Scopes.ORG_MEXICO)) {
    countryRoutes.push({ label: 'Mexico - Archive', value: 'MEX_ARCHIVE' });
    currentCountryRoute.push({ label: 'Mexico - Archive', value: 'MEX_ARCHIVE' });

    countryRoutes.push({ label: 'Mexico', value: 'MEX_SCI' });
    currentCountryRoute.push({ label: 'Mexico', value: 'MEX_SCI' });
  }

  if (access.includes(Scopes.ORG_DETECNO_MEX)) {
    countryRoutes.push({ label: 'Mexico v2', value: 'DETECNO_MEX' });
    currentCountryRoute.push({ label: 'Mexico v2', value: 'DETECNO_MEX' });
  }

  if (access.includes(Scopes.ORG_NEW_ZEALAND)) {
    countryRoutes.push({ label: 'New Zealand', value: 'NZL' });
    currentCountryRoute.push({ label: 'New Zealand', value: 'NZL' });
  }

  if (access.includes(Scopes.ORG_NIGERIA)) {
    countryRoutes.push({ label: 'Nigeria', value: 'NGA' });
    currentCountryRoute.push({ label: 'Nigeria', value: 'NGA' });
  }

  if (access.includes(Scopes.ORG_NORWAY)) {
    countryRoutes.push({ label: 'Norway', value: 'NOR' });
    currentCountryRoute.push({ label: 'Norway', value: 'NOR' });
  }

  if (access.includes(Scopes.ORG_OMAN)) {
    countryRoutes.push({ label: 'Oman', value: 'OMN' });
    currentCountryRoute.push({ label: 'Oman', value: 'OMN' });
  }

  if (access.includes(Scopes.ORG_PAKISTAN)) {
    countryRoutes.push({ label: 'Pakistan', value: 'PAK' });
    currentCountryRoute.push({ label: 'Pakistan', value: 'PAK' });
  }

  if (access.includes(Scopes.ORG_PHILIPPINES)) {
    countryRoutes.push({ label: 'Philippines', value: 'PHL' });
    currentCountryRoute.push({ label: 'Philippines', value: 'PHL' });
  }

  if (access.includes(Scopes.ORG_POLAND)) {
    countryRoutes.push({ label: 'Poland', value: 'POL' });
    currentCountryRoute.push({ label: 'Poland', value: 'POL' });
  }

  if (access.includes(Scopes.ORG_PORTUGAL)) {
    countryRoutes.push({ label: 'Portugal (Legacy)', value: 'PRT' });
    currentCountryRoute.push({ label: 'Portugal (Legacy)', value: 'PRT' });
  }
  if (access.includes(Scopes.ORG_PORTUGAL_DIRECT)) {
    countryRoutes.push({ label: 'Portugal', value: 'PRT_DIRECT' });
    currentCountryRoute.push({ label: 'Portugal', value: 'PRT_DIRECT' });
  }

  if (access.includes(Scopes.ORG_ROMANIA)) {
    countryRoutes.push({ label: 'Romania (Legacy)', value: 'ROU' });
    currentCountryRoute.push({ label: 'Romania (Legacy)', value: 'ROU' });
  }
  if (access.includes(Scopes.ORG_ROMANIA_DIRECT)) {
    countryRoutes.push({ label: 'Romania', value: 'ROU_DIRECT' });
    currentCountryRoute.push({ label: 'Romania', value: 'ROU_DIRECT' });
  }

  if (access.includes(Scopes.ORG_SINGAPORE)) {
    countryRoutes.push({ label: 'Singapore', value: 'SGP' });
    currentCountryRoute.push({ label: 'Singapore', value: 'SGP' });
  }

  if (access.includes(Scopes.ORG_SPAIN_CANARY_ISLANDS)) {
    countryRoutes.push({ label: 'Canary Islands - SII', value: 'ESP_CI_SII' });
    currentCountryRoute.push({ label: 'Canary Islands - SII', value: 'ESP_CI_SII' });
  }

  if (access.includes(Scopes.ORG_SPAIN)) {
    countryRoutes.push({ label: 'Spain - SII', value: 'ESP_SII' });
    currentCountryRoute.push({ label: 'Spain - SII', value: 'ESP_SII' });
  }

  if (access.includes(Scopes.ORG_SPAIN_VERIFACTU)) {
    countryRoutes.push({ label: 'Spain - VeriFactu', value: 'ESP_VERIFACTU' });
    currentCountryRoute.push({ label: 'Spain - VeriFactu', value: 'ESP_VERIFACTU' });
  }

  if (access.includes(Scopes.ORG_SAUDI_ARABIA)) {
    // TODO: once certified, change Direct to Saudi Arabia
    countryRoutes.push({ label: 'Saudi Arabia - Direct', value: 'SAU' });
    currentCountryRoute.push({ label: 'Saudi Arabia - Direct', value: 'SAU' });
    // TODO: once certified, remove
    countryRoutes.push({ label: 'Saudi Arabia', value: 'SAU_INVOICEQ' });
    currentCountryRoute.push({ label: 'Saudi Arabia', value: 'SAU_INVOICEQ' });
  }

  if (access.includes(Scopes.ORG_TAIWAN)) {
    countryRoutes.push({ label: 'Taiwan', value: 'TWN' });
    currentCountryRoute.push({ label: 'Taiwan', value: 'TWN' });
  }

  if (access.includes(Scopes.ORG_TURKEY)) {
    countryRoutes.push({ label: 'Turkey', value: 'TUR' });
    currentCountryRoute.push({ label: 'Turkey', value: 'TUR' });
  }

  if (access.includes(Scopes.ORG_SOUTH_KOREA)) {
    countryRoutes.push({ label: 'South Korea', value: 'KOR' });
    currentCountryRoute.push({ label: 'South Korea', value: 'KOR' });
  }

  if (access.includes(Scopes.ORG_UAE)) {
    countryRoutes.push({ label: 'United Arab Emirates', value: 'ARE' });
    currentCountryRoute.push({ label: 'United Arab Emirates', value: 'ARE' });

    countryRoutes.push({ label: 'United Arab Emirates - Tax Authority', value: 'ARE_TA' });
    currentCountryRoute.push({ label: 'United Arab Emirates - Tax Authority', value: 'ARE_TA' });

    countryRoutes.push({ label: 'United Arab Emirates - Peppol', value: 'ARE_PEPPOL' });
    currentCountryRoute.push({ label: 'United Arab Emirates - Peppol', value: 'ARE_PEPPOL' });
  }

  // Utility routes
  if (access.includes(Scopes.ORG_LEGACY_DATA)) {
    utilityRoutes.push({ label: 'Legacy Data Import', value: 'LEGACY_DATA' });
  }

  const bypass = isAdminOrDeveloper;
  // Visible when released to this stage and not admin-only (tier null). Admins see everything; a
  // user explicitly granted an internal route's scope keeps it too.
  const heldByScope = (value: string): boolean =>
    !!userAccess?.scopes?.includes(`org:${value.toLowerCase()}` as Scopes) ||
    (value === 'FRA_OUTPOST' && !!userAccess?.scopes?.includes(Scopes.ORG_FRANCE_OUTPOST));
  const onStage = (value: string): boolean =>
    bypass ||
    !opts.stage ||
    value === currentRoute ||
    (isRouteOnStage(value, opts.stage) && (!isAdminOnlyRoute(value) || heldByScope(value)));
  // Tier lock only (stage is handled by onStage). Admins get the mark too: the badge tells them they
  // are granting a route above the org's plan; Task 5 decides whether selection is blocked.
  const lockFor = (value: string): Pick<ReactSelectGroup, 'locked' | 'requiredPlan'> => {
    if (opts.plan === undefined || value === currentRoute) return {};
    const requiredPlan = requiredPlanForRoute(value);
    if (!requiredPlan || requiredPlan === 'Free') return {};
    const planMax = PLAN_MAX_TIER[(opts.plan as OrganisationPlan) ?? 'Free'] ?? 1;
    return planMax >= PLAN_MAX_TIER[requiredPlan] ? {} : { locked: true, requiredPlan };
  };
  const isOwn = (value: string): boolean => !!opts.ownCountry && routeTier(value)?.country === opts.ownCountry;

  const visibleNetworkRoutes = networkRoutes.filter((r) => onStage(r.value));
  const visibleConnectorRoutes = connectorRoutes.filter((r) => onStage(r.value));
  const visibleUtilityRoutes = utilityRoutes.filter((r) => onStage(r.value));
  // The legacy seed (India/Japan/Mexico) overlaps the scope-driven pushes, so dedupe by value.
  const visibleCountryRoutes = uniqBy(countryRoutes, 'value').filter((r) => onStage(r.value));
  const visibleCurrentCountryRoute = currentCountryRoute.filter((r) => onStage(r.value));

  const testRoutes: ReactSelectOption[] = [
    {
      label: 'Test',
      value: 'MCK'
    }
  ];

  // Add MOCK to special named routes
  const testRoutesFromCurrentRoutes = [
    ...visibleNetworkRoutes,
    ...visibleConnectorRoutes,
    ...sortBy(visibleCountryRoutes, 'label')
  ]
    .filter((route) => !excludeLiveOnlyMocks || !LIVE_ONLY_ROUTE_VALUES.has(route.value))
    .map((route) => ({
      label: TEST_LABEL_OVERRIDES[route.value] ?? `${route.label} (Test)`,
      value: `${route.value}_MOCK`
    }));

  // An org already on a test twin whose live route is hidden (admin-only or not on this stage) must
  // still see its own value, otherwise the select renders blank; the live route itself stays hidden.
  if (currentRoute?.endsWith('_MOCK') && !testRoutesFromCurrentRoutes.some((r) => r.value === currentRoute)) {
    const base = currentRoute.slice(0, -5);
    const known = [...networkRoutes, ...connectorRoutes, ...countryRoutes, ...utilityRoutes].find(
      (r) => r.value === base
    );
    const label = known?.label ?? Routes[base as keyof typeof Routes];
    if (label) {
      testRoutesFromCurrentRoutes.push({ label: TEST_LABEL_OVERRIDES[base] ?? `${label} (Test)`, value: currentRoute });
    }
  }

  let networkRoutesFinal = [];
  if (networkRoutes.length > 0) {
    networkRoutesFinal = visibleNetworkRoutes.map((route) => ({
      ...route,
      group: 'Networks',
      ...lockFor(route.value)
    }));
  }

  const connectorRoutesFinal = visibleConnectorRoutes.map((route) => ({
    ...route,
    group: 'Connectors',
    ...lockFor(route.value)
  }));

  let utilityRoutesFinal = [];
  if (visibleUtilityRoutes.length > 0) {
    utilityRoutesFinal = visibleUtilityRoutes.map((route) => ({
      ...route,
      group: 'Utilities',
      ...lockFor(route.value)
    }));
  }

  // Countries
  let countryRoutesFinal = [];
  if (
    hasPaidPlan ||
    userAccess?.groups?.includes(CoreUserGroups.ADMIN) ||
    userAccess?.groups?.includes(CoreUserGroups.DEVELOPER)
  ) {
    countryRoutesFinal = sortBy(visibleCountryRoutes, 'label')
      .map((route) => ({ ...route, group: 'Country specific', ...lockFor(route.value) }))
      .sort((a, b) => Number(isOwn(b.value)) - Number(isOwn(a.value)));
  }
  // current country
  else {
    // Only add if it contains a route

    if (visibleCurrentCountryRoute.length > 0)
      countryRoutesFinal = sortBy(visibleCurrentCountryRoute, 'label')
        .map((route) => ({ ...route, group: 'Country specific', ...lockFor(route.value) }))
        .sort((a, b) => Number(isOwn(b.value)) - Number(isOwn(a.value)));
  }

  // Test and QA
  const testRoutesFinal = [...testRoutes, ...testRoutesFromCurrentRoutes].map((route) => ({
    ...route,
    group: 'Test and QA'
  }));

  return [
    ...networkRoutesFinal,
    ...connectorRoutesFinal,
    ...countryRoutesFinal,
    ...testRoutesFinal,
    ...utilityRoutesFinal
  ];
};

export const buildActionSelectOptions = (
  userAccess: {
    scopes?: Scopes[];
    groups?: CoreUserGroups[];
  },
  currentRoute: string = null,
  hasPaidPlan: boolean = true,
  excludeLiveOnlyMocks: boolean = false
): ReactSelectGroup[] => {
  const access = currentRoute ? [...userAccess.scopes, `org:${currentRoute.toLowerCase()}`] : userAccess.scopes;
  const networkRoutes: ReactSelectOption[] = [];

  if (access.includes(Scopes.ORG_APOC)) {
    networkRoutes.push({ label: 'APOC', value: 'APOC' });
  }
  if (access.includes(Scopes.ORG_SAP_CONCUR)) {
    networkRoutes.push({ label: 'SAP Concur', value: 'SAP_CONCUR' });
  }
  if (access.includes(Scopes.ORG_YOKOY)) {
    networkRoutes.push({ label: 'Yokoy', value: 'YOKOY' });
  }
  if (access.includes(Scopes.ORG_BIRCHSTREET)) {
    networkRoutes.push({ label: 'BirchStreet', value: 'BIRCHSTREET' });
  }
  if (access.includes(Scopes.ORG_EMAIL_ACTION)) {
    networkRoutes.push({ label: 'Email', value: 'EMAIL' });
  }

  const countryRoutes: ReactSelectOption[] = [];

  const currentCountryRoute: ReactSelectOption[] = [];

  const testRoutes: ReactSelectOption[] = [
    {
      label: 'Test',
      value: 'MCK'
    }
  ];

  // Add MOCK to special named routes
  const testRoutesFromCurrentRoutes = [...networkRoutes, ...sortBy(countryRoutes, 'label')]
    .filter((route) => !excludeLiveOnlyMocks || !LIVE_ONLY_ROUTE_VALUES.has(route.value))
    .map((route) => ({
      label: TEST_LABEL_OVERRIDES[route.value] ?? `${route.label} (Test)`,
      value: `${route.value}_MOCK`
    }));

  let networkRoutesFinal = [];
  if (networkRoutes.length > 0) {
    networkRoutesFinal = [...networkRoutes].map((route) => ({
      ...route,
      group: 'Networks'
    }));
  }

  // Countries
  let countryRoutesFinal = [];
  if (
    hasPaidPlan ||
    userAccess?.groups?.includes(CoreUserGroups.ADMIN) ||
    userAccess?.groups?.includes(CoreUserGroups.DEVELOPER)
  ) {
    countryRoutesFinal = sortBy(countryRoutes, 'label').map((route) => ({
      ...route,
      group: 'Country specific'
    }));
  }
  // current country
  else {
    // Only add if it contains a route

    if (currentCountryRoute.length > 0)
      countryRoutesFinal = sortBy(currentCountryRoute, 'label').map((route) => ({
        ...route,
        group: 'Country specific'
      }));
  }

  // Test and QA
  const testRoutesFinal = [...testRoutes, ...testRoutesFromCurrentRoutes].map((route) => ({
    ...route,
    group: 'Test and QA'
  }));

  return [...networkRoutesFinal, ...countryRoutesFinal, ...testRoutesFinal];
};

/**
 * Return short name for Mock routes and routes with country extended names
 */
export enum TestRoutes {
  // testing aliases

  MOCK = 'Test',
  /**
   * @deprecated Use MOCK instead.
   */

  MCK = 'Test',
  /**
   * @deprecated Use MOCK instead.
   */

  TEST = 'Test',

  IND_MOCK = 'IND (Test)',

  // PEPPOL routes

  PEPPOL = 'Peppol',
  PEPPOL_MOCK = 'Peppol (Test)',

  APOC = 'APOC',
  APOC_MOCK = 'APOC (Test)',

  ARE_TA = 'ARE - Tax Authority',
  ARE_TA_MOCK = 'ARE - Tax Authority (Test)',
  ARE_PEPPOL = 'ARE - Peppol',
  ARE_PEPPOL_MOCK = 'ARE - Peppol (Test)',

  BEL = 'BEL',
  BEL_MOCK = 'BEL (Test)',

  DETECNO_MEX = 'MEX v2',
  DETECNO_MEX_MOCK = 'MEX v2 (Test)',

  DEU_XRECHNUNG = 'DEU - XRechnung',
  DEU_XRECHNUNG_MOCK = 'DEU - XRechnung (Test)',

  DEU_ZUGFERD = 'DEU - ZUGFeRD',
  DEU_ZUGFERD_MOCK = 'DEU - ZUGFeRD (Test)',

  DNK_NEMHANDEL = 'DNK - NemHandel',
  DNK_NEMHANDEL_MOCK = 'DNK - NemHandel (Test)',

  DNK_PEPPOL = 'DNK - PEPPOL',
  DNK_PEPPOL_MOCK = 'DNK - PEPPOL (Test)',

  FRA_EINVOICE = 'FRA - e-invoicing',
  FRA_EINVOICE_MOCK = 'FRA - e-invoicing (Test)',

  FRA_EREPORT = 'FRA - e-reporting',
  FRA_EREPORT_MOCK = 'FRA - e-reporting (Test)',

  FRA_OUTPOST = 'FRA - Outpost',

  // TODO: once certified, remove
  SAU_INVOICEQ = 'SAU',
  SAU = 'SAU - Direct',
  SAU_MOCK = 'SAU - Direct (Test)',

  // SOVOS routes
  CHL_SCI = 'CHL',

  ESP_CI_SII = 'ESP CI - SII',
  ESP_CI_SII_MOCK = 'ESP CI - SII (Test)',

  ESP_SII = 'ESP - SII',
  ESP_SII_MOCK = 'ESP - SII (Test)',

  ESP_VERIFACTU = 'ESP - VeriFactu',
  ESP_VERIFACTU_MOCK = 'ESP - VeriFactu (Test)',

  MEX_ARCHIVE = 'MEX - Archiving',
  MEX_ARCHIVE_MOCK = 'MEX - Archiving (Test)',

  MEX_SCI = 'MEX',

  MEX_SCI_MOCK = 'MEX (Test)',

  MEX_SLI = 'MEX',

  MYS_PEPPOL = 'MYS - PEPPOL',
  MYS_PEPPOL_MOCK = 'MYS - PEPPOL (Test)',

  MYS_TA = 'MYS - Tax Authority',
  MYS_TA_MOCK = 'MYS - Tax Authority (Test)',

  ITA = 'ITA - Legacy',
  ITA_MOCK = 'ITA - Legacy (Test)',

  ITA_DIRECT = 'ITA',
  ITA_DIRECT_MOCK = 'ITA (Test)',

  ITA_SCI = 'ITA',
  ITA_SCI_MOCK = 'ITA (Test)',

  ROU = 'ROU - Legacy',
  ROU_MOCK = 'ROU - Legacy (Test)',

  ROU_DIRECT = 'ROU',
  ROU_DIRECT_MOCK = 'ROU (Test)',

  LEGACY_DATA = 'Legacy Data'
}

/**
 * Return full name for routes with country extended/special names
 */
export enum TestRoutesFullName {
  // testing aliases

  MOCK = 'Test',
  /**
   * @deprecated Use MOCK instead.
   */

  MCK = 'Test',
  /**
   * @deprecated Use MOCK instead.
   */

  TEST = 'Test',

  // PEPPOL routes
  PEPPOL = 'Peppol',
  PEPPOL_MOCK = 'Peppol (Test)',

  APOC = 'APOC',
  APOC_MOCK = 'APOC (Test)',
  // BEL = 'Belgium',
  // BEL_MOCK = 'Belgium (Test)',

  CHL_SCI = 'Chile',

  DNK_NEMHANDEL = 'Denmark (NemHandel)',
  DNK_NEMHANDEL_MOCK = 'Denmark (NemHandel Test)',
  DNK_PEPPOL = 'Denmark (PEPPOL)',
  DNK_PEPPOL_MOCK = 'Denmark (PEPPOL Test)',

  DETECNO_MEX = 'Mexico v2',
  DETECNO_MEX_MOCK = 'Mexico v2 (Test)',

  DEU_XRECHNUNG = 'Germany - XRechnung',
  DEU_XRECHNUNG_MOCK = 'Germany - XRechnung (Test)',
  DEU_ZUGFERD = 'Germany - ZUGFeRD',
  DEU_ZUGFERD_MOCK = 'Germany - ZUGFeRD (Test)',

  ESP_CI_SII = 'Canary Islands - SII',
  ESP_CI_SII_MOCK = 'Canary Islands - SII (Test)',

  ESP_SII = 'Spain - SII',
  ESP_SII_MOCK = 'Spain - SII (Test)',

  MEX_ARCHIVE = 'MEX - Archiving',
  MEX_ARCHIVE_MOCK = 'MEX - Archiving (Test)',
  MEX_SCI = 'Mexico',
  MEX_SCI_MOCK = 'Mexico (Test)',
  MEX_SLI = 'Mexico',

  MYS_PEPPOL = 'MYS - PEPPOL',
  MYS_PEPPOL_MOCK = 'MYS - PEPPOL (Test)',
  MYS_TA = 'MYS - Tax Authority',
  MYS_TA_MOCK = 'MYS - Tax Authority (Test)',

  LEGACY_DATA = 'Legacy Data',

  ITA_DIRECT = 'Italy - Direct',
  ITA_DIRECT_MOCK = 'Italy - Direct (Test)',

  ITA_SCI = 'Italy',
  ITA_SCI_MOCK = 'Italy (Test)',

  ROU_DIRECT = 'Romania',
  ROU_DIRECT_MOCK = 'Romania (Test)',
  ROU = 'Romania - Legac',
  ROU_MOCK = 'Romania - Legacy (Test)',

  SAU_MOCK = 'Saudi Arabia (Test)',
  SAU_INVOICEQ = 'Saudi Arabia',
  SAU_INVOICEQ_MOCK = 'Saudi Arabia (Test)',

  // ARE_MOCK = 'United Arab Emirates (Test)',
  ARE_TA = 'United Arab Emirates Tax Authority',
  ARE_TA_MOCK = 'United Arab Emirates Tax Authority (Test)',
  ARE_PEPPOL = 'United Arab Emirates Peppol',
  ARE_PEPPOL_MOCK = 'United Arab Emirates Peppol (Test)',

  FRA_REPORT = 'France E-Reporting'
}
/**
 * Return full name for Mock routes and routes with country extended or special names
 */
export const getTestRouteFullName = (route: string): string => {
  if (route in TestRoutesFullName) {
    return TestRoutesFullName[route as keyof typeof TestRoutesFullName];
  }

  if (route?.includes('MOCK')) {
    const rtnRoute = Countries[route.replace('_MOCK', '')];
    if (rtnRoute) {
      return `${rtnRoute} (Test)`;
    }
  }

  return null;
};

export const removeEmptyValues = <T extends object>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj)
      .filter(([, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Recursively clean nested objects
          const cleanedValue = removeEmptyValues(value);
          return Object.keys(cleanedValue).length > 0;
        }
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== null && value !== undefined && value !== '';
      })
      .map(([key, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Recursively clean nested objects
          return [key, removeEmptyValues(value)];
        }
        if (Array.isArray(value)) {
          return [key, value.map((item) => (item && typeof item === 'object' ? removeEmptyValues(item) : item))];
        }
        return [key, value];
      })
  ) as Partial<T>;

export const parseLineItemToDocumentLine = (line: any) => {
  // Detect if already in new format
  const isNewFormat = 'quantity' in line && typeof line.price === 'object' && typeof line.vat === 'object';

  if (isNewFormat) {
    return line;
  }

  const { description, qty, type, code, vat, price } = line;
  return {
    description: description,
    quantity: qty || 0,
    price: {
      amount: price || 0
    },
    vat: {
      type,
      amount: vat || 0,
      code
    }
  };
};

export const parseLineItemsToDocumentLines = (lines: any) => lines.map((item) => parseLineItemToDocumentLine(item));

// Use eezi-utils to calculate Invoice Totals
export const calculateInvoiceTotals = (lineItems) => {
  const docLineItems = parseLineItemsToDocumentLines(lineItems);
  return calculateDocumentTotals(docLineItems);
};

export const calculateInvoiceTotalsWithAdditionalTax = (lineItems) => {
  let vatTotal = 0;
  let subtotal = 0;

  lineItems.forEach((item) => {
    const quantity = item.quantity || item.qty || 0;
    const price = item.price.amount ?? item.price;
    const vatType = item.vat?.type || item.type;
    const vatAmount = item.vat?.amount ?? item.vat;
    const addVatType = item.additionalTaxesType;
    const addVatAmount = item.additionalTaxesVat ?? 0;

    const itemTotal = quantity * price;
    subtotal += itemTotal;

    if (vatType === 'percent') {
      // Normal VAT
      if (addVatAmount === 0) {
        // Round VAT amount
        vatTotal += symmetricRound((vatAmount / 100) * itemTotal, 2);
      }
      // Additional VAT on top of normal vAT
      else if (addVatAmount > 0) {
        const normVat = symmetricRound((vatAmount / 100) * itemTotal, 2);

        if (addVatType === 'percent') {
          const addVat = symmetricRound((addVatAmount / 100) * (itemTotal + normVat), 2);

          vatTotal += normVat + addVat;
        } else if (addVatType === 'fixed') {
          const addVat = normVat + addVatAmount;

          vatTotal += normVat + addVat;
        }
      }
    } else if (vatType === 'fixed') {
      // Normal VAT
      if (addVatAmount === 0) {
        vatTotal += quantity * vatAmount;
      }
      // Additional VAT on top of normal vAT
      else if (addVatAmount > 0) {
        const normVat = quantity * vatAmount;

        if (addVatType === 'percent') {
          const addVat = symmetricRound((addVatAmount / 100) * (itemTotal + normVat) * 100) / 100;
          vatTotal += normVat + addVat;
        } else if (addVatType === 'fixed') {
          const addVat = quantity * addVatAmount;
          vatTotal += normVat + addVat;
        }
      }
    }
  });

  // Round total
  const total = symmetricRound(subtotal + vatTotal, 2);

  return {
    vatTotal,
    subtotal,
    total
  };
};

// Use eezi-utils to calculate Line Tax Amount
export const calculateLineTaxAmount = (lineItem) => calculateLineTax(parseLineItemToDocumentLine(lineItem));

export const calculateLineTaxPercentage = (lineItem) => {
  let vatPerc = 0;

  const price = lineItem.price.amount ?? lineItem.price;
  const vatType = lineItem.vat?.type || lineItem.type;
  const vatAmount = lineItem.vat?.amount ?? lineItem.vat;

  if (vatType === 'percent') {
    vatPerc = vatAmount;
  } else if (vatType === 'fixed') {
    vatPerc = (vatAmount / price) * 100;
  }

  return symmetricRound(vatPerc);
};

export const getRequiredFields = (schema: Yup.ObjectSchema<any>, values?: Record<string, any>): string[] => {
  const requiredFields: string[] = [];

  Object.entries(schema.fields).forEach(([key, field]) => {
    // Resolve `.when()` conditions against the current form values so conditional required fields are included
    const typedField = values
      ? (field as AnySchema).resolve({ value: values[key], parent: values })
      : (field as AnySchema);
    // String schemas add a 'required' test; other types (e.g. number) only mark the spec as non-optional
    if (typedField.tests.some((test) => test.OPTIONS.name === 'required') || typedField.spec?.optional === false) {
      requiredFields.push(key);
    }
  });

  return requiredFields;
};

export type CurrencyStats = {
  success: number;
  pending: number;
  total: number; // count across all statuses, this currency
  totalValue: number; // success value only
  errors: number;
  successTrend: number; // Percent increase/decrease from prev period
  errorTrend: number;
  totalTrend: number; // Percent change in total count for this currency
  totalValueTrend: number; // Percent change in value for success
};

export type AggregateStats = {
  success: number;
  pending: number;
  total: number;
  errors: number;
  successTrend: number;
  errorTrend: number;
  totalTrend: number;
};

export type TypeStats = {
  aggregate: AggregateStats;
  currencies: StringTMap<CurrencyStats>;
};

// Backwards-compat alias used by reducers that hold either an aggregate or a per-currency stats object
export type Stats = CurrencyStats;

// Extract available stat options
export const getAvailableStatOptions = (
  data: Record<InvoiceType, TypeStats>,
  type?: InvoiceType,
  includeSent?: boolean,
  includeReceived?: boolean
): {
  types: InvoiceType[];
  currencies: string[];
} => {
  if (!data || Object.keys(data).length === 0) {
    return { types: [], currencies: [] };
  }
  let types = Object.keys(data) as InvoiceType[];

  // Include sent from feature flag
  if (includeSent && !types.includes(InvoiceType.SENT)) {
    types.push(InvoiceType.SENT);
  } else if (includeSent === false) {
    types = types.filter((t) => t !== InvoiceType.SENT);
  }
  // Include received from feature flag
  if (includeReceived && !types.includes(InvoiceType.RECEIVED)) {
    types.push(InvoiceType.RECEIVED);
  } else if (includeReceived === false) {
    types = types.filter((t) => t !== InvoiceType.RECEIVED);
  }

  // Sort types alphabetically
  types = [...new Set(types)].sort((a, b) => b.localeCompare(a));

  const defaultType = type || types[0];
  const currencies = data?.[defaultType]?.currencies;
  if (defaultType && currencies && Object.keys(currencies).length > 0) {
    return {
      types,
      currencies: Object.keys(currencies)
    };
  }
  return {
    types,
    currencies: []
  };
};

const parseInvoiceRefs = (
  invoiceRefs: string[]
): {
  [key: string]: string;
}[] =>
  invoiceRefs.map((ref, index) => ({
    [index]: ref
  }));

const flatten = (json: { [t: string]: any }, prefix = ''): { [t: string]: string } => {
  let temp: { [t: string]: string } = {};

  temp = Object.keys(json).reduce((memo, key: string) => {
    if (['function', 'symbol', 'undefined'].includes(typeof json[key])) {
      return memo;
    }

    if (json[key] === null) {
      // Treat null as an empty string
      // eslint-disable-next-line no-param-reassign
      memo[`${prefix}${key}`] = '';
      return memo;
    }

    if (typeof json[key] === 'object') {
      // Handle arrays
      if (Array.isArray(json[key])) {
        // Parse string arrays ["123", "456"] to [{"0": "123"}, {"1": "456"}] for example invoiceRefs
        if (json[key].every((item) => typeof item === 'string')) {
          const tmpJson = parseInvoiceRefs(json[key]);
          tmpJson.forEach((obj: { [t: string]: any }) => {
            // eslint-disable-next-line no-param-reassign
            memo = { ...memo, ...flatten(obj, `${prefix}${key}.`) };
          });
          return memo;
        }

        json[key].forEach((obj: { [t: string]: any }, index: number) => {
          // eslint-disable-next-line no-param-reassign
          memo = { ...memo, ...flatten(obj, `${prefix}${key}.${index}.`) };
        });

        return memo;
      }
      // Handle nested objects
      // eslint-disable-next-line no-param-reassign
      memo = { ...memo, ...flatten(json[key], `${prefix}${key}.`) };
      return memo;
    }

    // Handle regular values
    // eslint-disable-next-line no-param-reassign
    memo[`${prefix}${key}`] = json[key].toString();

    return memo;
  }, temp);

  return temp;
};

// Fields containing both " and , causes CSV values to be mis aligned with headers
export const sanitizeCsvValue = (val: string): string => {
  if (val?.includes('"') && val?.includes(',')) {
    return val.replaceAll('"', "'");
  }
  return val;
};

// Function to convert JSON array to CSV
export const jsonToCsv = (json: { [t: string]: any }[]): string => {
  const excludedKeys = ['customInfo.sovos.notifications', 'customInfo.mex.docUrl', 'country'];
  const excludePartialKeys = ['attachments.', 'documents.', 'customInfo.ta'];
  const excludeKeysExceptions = [
    'customInfo.taTWN.migNumber',
    'customInfo.taMUS.invoiceCounter',
    'customInfo.taMUS.previousHash'
  ];

  const flat = json.map((m) => flatten(m));

  // Collect headers, excluding keys
  const headers = Object.keys(
    flat.reduce((hdrs: { [t: string]: boolean }, cur) => {
      Object.keys(cur).forEach((key) => {
        const isIncluded = excludeKeysExceptions.includes(key);
        const shouldExclude =
          !isIncluded &&
          (excludedKeys.includes(key) || excludePartialKeys.some((partialKey) => key.startsWith(partialKey)));

        if (!shouldExclude) {
          // eslint-disable-next-line no-param-reassign
          hdrs[key] = true;
        }
      });
      return hdrs;
    }, {})
  );

  const result: string[] = [`"${headers.join('","')}"`]; // CSV header row

  flat.forEach((f) => {
    const temp: string[] = [];
    headers.forEach((header) => {
      const raw = f[header] || ''; // Exclude based on excludedKeys and excludePartialKeys
      // F6 — pentest fix: neutralise CSV formula-trigger chars (= + - @) before
      // re-applying the existing quote-collision sanitiser.
      const value = sanitiseCsvCell(raw);
      temp.push(`"${sanitizeCsvValue(value)}"`);
    });
    result.push(temp.join(',')); // CSV data row
  });

  return result.join('\n');
};

export const getFilterValues = (filters: SearchFiltersFormFields): string[] => {
  const hydratedFilters = [];
  const dateField = filters?.dateField || 'issued';

  // Date
  if (filters?.date) {
    // NB. Add 1 day due to ISO 8601 one standard as Monday start of the week
    if (filters.date === 'today') {
      const dateFrom = dayjs().utc().startOf('day').toISOString();
      const dateTo = dayjs().utc().endOf('day').toISOString();
      hydratedFilters.push(`${dateField}:[${dateFrom} TO ${dateTo}]`);
    }
    if (filters.date === 'this-week') {
      // This week: Monday of current week to now
      const dateFrom = dayjs().utc().startOf('isoWeek').startOf('day').toISOString();
      const toDate = '*';
      hydratedFilters.push(`${dateField}:[${dateFrom} TO ${toDate}]`);
    }
    if (filters.date === 'last-week') {
      // Last week: Monday to Sunday of the previous week
      const lastWeek = dayjs().utc().subtract(1, 'week');
      const dateFrom = lastWeek.startOf('isoWeek').startOf('day').toISOString();
      const toDate = lastWeek.endOf('isoWeek').endOf('day').toISOString();
      hydratedFilters.push(`${dateField}:[${dateFrom} TO ${toDate}]`);
    }
    if (filters.date === 'this-month') {
      const dateFrom = dayjs().utc().startOf('month').startOf('day').toISOString();
      const toDate = '*';
      hydratedFilters.push(`${dateField}:[${dateFrom} TO ${toDate}]`);
    }
    if (filters.date === 'last-month') {
      const dateFrom = dayjs().utc().subtract(1, 'month').startOf('month').startOf('day').toISOString();
      const toDate = dayjs().utc().subtract(1, 'month').endOf('month').endOf('day').toISOString();
      hydratedFilters.push(`${dateField}:[${dateFrom} TO ${toDate}]`);
    }
    if (filters.date === 'custom' && filters['date-from']) {
      const toDate = filters['date-to'] || '*';
      hydratedFilters.push(`${dateField}:[${filters['date-from']} TO ${toDate}]`);
    }
  }

  // Type
  if (filters?.type) {
    hydratedFilters.push(`type:${filters.type}`);
  }

  // Status
  if (filters?.status) {
    // Rollup pending submission and pending response to pending
    hydratedFilters.push(
      filters.status === InvoiceStatus.PENDING_SUBMISSION
        ? `(status:"${InvoiceStatus.PENDING_SUBMISSION}" OR status:"${InvoiceStatus.PENDING_RESPONSE}")`
        : `status:"${filters.status}"`
    );
  }

  // Amount
  if (filters?.amount) {
    hydratedFilters.push(`total.amount:${Number(filters.amount)}`);
  }

  // Currency
  if (filters?.currency) {
    hydratedFilters.push(`total.currency:${filters.currency}`);
  }

  // Route
  if (filters?.route) {
    hydratedFilters.push(`route:${filters.route}`);
  }

  return hydratedFilters;
};

export enum SchemeAgencyIdOptionsARE {
  'TL' = 'Commercial/Trade license',
  'EID' = 'Emirates ID',
  'PAS' = 'Passport',
  'CD' = 'Cabinet Decision'
}

export enum TransactionTypeOptionsARE {
  // @ts-ignore
  '00' = 'Standard Invoice',
  // @ts-ignore
  '01' = 'Free Trade Zone',
  // @ts-ignore
  '02' = 'Deemed Supply',
  // @ts-ignore
  '03' = 'Profit Margin Scheme',
  // @ts-ignore
  '04' = 'Summary Invoice',
  // @ts-ignore
  '05' = 'Continuous Supply',
  // @ts-ignore
  '06' = 'Disclosed Agent Billing',
  // @ts-ignore
  '07' = 'Supply through e-commerce',
  // @ts-ignore
  '08' = 'Exports'
}

// tax code = IDN-TD.00307
export enum ExemptReasonCodeIDN7 {
  'IDN-TD.00501' = 'Free Trade Zone',
  'IDN-TD.00502' = 'The Bonded Zone',
  'IDN-TD.00503' = 'Grants and Foreign Aid',
  'IDN-TD.00504' = 'Jet Fuel',
  'IDN-TD.00505' = 'Others',
  'IDN-TD.00506' = 'Coal CoW Gen. 1 Contractors',
  'IDN-TD.00507' = 'Deliveries of Fuels for Foreign Ships',
  'IDN-TD.00508' = 'Deliveries of Taxable Service related to Certain Transportations',
  'IDN-TD.00509' = 'Deliveries of Taxable Goods in the Special Economic Zone',
  'IDN-TD.00510' = 'Deliveries of Anode Slime as a Strategic Goods',
  'IDN-TD.00511' = 'Deliveries of Certain Transportations and/or Taxable Services related to Certain Transportations',
  'IDN-TD.00512' = 'Deliveries to PSC Contractors as defined by Government Regulation No. 27 Year 2017',
  'IDN-TD.00513' = 'Deliveries of Landed House and Vertical House Borned by Government in the Fiscal Year of 2021',
  'IDN-TD.00514' = 'Deliveries of Space/Building Rental Services to Retailers that is Borned by Government in the Fiscal Year of 2021',
  'IDN-TD.00515' = 'Deliveries of Goods and Services in relation to the Handlings of COVID-19 Pandemics (MoFR 239/PMK.03/2020)',
  // eslint-disable-next-line max-len
  'IDN-TD.00516' = 'MoFR 103/PMK.010/2021 Incentives in the form of VAT of Deliveries of Landed House and Vertical House that is Borned by Government in the Fiscal Year of 2021',
  'IDN-TD.00517' = 'Special Economic Zone Government Regulation number 40 of 2021',
  'IDN-TD.00518' = 'Free Trade Zone Government Regulation number 41 of 2021',
  'IDN-TD.00519' = 'Handover of Treaded Houses and Residential Units of Flats Borne by the Government for Fiscal Year 2022',
  'IDN-TD.00520' = 'VAT borne by the government in the context of Handling the Corona Virus Pandemic',
  'IDN-TD.00521' = 'Submission to Oil and Gas Cooperation Contractors that follow the provisions of Government Regulation Number 53 of 2017',
  'IDN-TD.00522' = 'Certain Taxable Goods which is strategic in the form of slime anode and granulated gold',
  'IDN-TD.00523' = 'for the submission of newsprint and/or magazine paper',
  'IDN-TD.00524' = 'Other VAT borne by the government',
  'IDN-TD.00525' = 'Certain Taxable Goods and Services',
  'IDN-TD.00526' = 'Delivery of Taxable Goods and Services in New Capitol City',
  'IDN-TD.00527' = 'Delivery of Electricity Vehicle with Battery based',
  'IDN-TD.00528' = 'Insentif Tambahan Penyerahan Rumah Tapak dan Satuan Rumah Susun Rumah Susun Ditanggung Pemerintah Tahun Anggaran 2025',
  'IDN-TD.00529' = 'PPN atas Penyerahan Hewan Khusus Tertentu Berupa Kuda serta Perlengkapan Pendukungnya Pemerintah Tahun Anggaran 2025',
  'IDN-TD.00530' = 'PPN atas Penyerahan Bekal Khusus Operasi Tertentu Yang Ditanggung Pemerintah Tahun Anggaran 2025',
  'IDN-TD.0101' = 'VALUE ADDED TAX IS NOT COLLECTED BASED ON PP NUMBER 10 OF 2012',
  'IDN-TD.0102' = 'Value Added Tax or Value Added Tax and Sales Tax on Luxury Goods is not collected',
  'IDN-TD.0103' = 'VALUE ADDED TAX AND SALES TAX ON LUXURY GOODS NOT COLLECTED',
  'IDN-TD.0104' = 'VALUE ADDED TAX IS NOT COLLECTED ACCORDING TO PP NUMBER 71 OF 2012',
  'IDN-TD.0105' = 'No stamp',
  'IDN-TD.0106' = 'VAT and / or VAT and STLG are not collected based on PMK No. 194 / PMK.03 / 2012',
  'IDN-TD.0107' = 'VAT NOT COLLECTED BASED ON PP NUMBER 15 OF 2015',
  'IDN-TD.0108' = 'VAT NOT COLLECTED BASED ON PP NUMBER 69 OF 2015',
  'IDN-TD.0109' = 'VAT NOT COLLECTED BASED ON PP NUMBER 96 OF 2015',
  'IDN-TD.0110' = 'VAT NOT COLLECTED BASED ON PP NUMBER 106 OF 2015',
  'IDN-TD.0111' = 'VAT NOT COLLECTED ACCORDING TO PP NUMBER 50 OF 2019',
  'IDN-TD.0112' = 'VAT OR VAT AND PPnBM NOT COLLECTED ACCORDING TO PP NUMBER 27 OF 2017',
  'IDN-TD.0113' = 'PPN DITANGGUNG PEMERINTAH EKSEKUSI PMK NOMOR 13 TAHUN 2025',
  'IDN-TD.0114' = 'VAT borne by the GOVERNMENT OF EX PMK 102 / PMK.010 / 2021',
  'IDN-TD.0115' = 'VAT borne by the GOVERNMENT OF EX PMK 239 / PMK.03 / 2020',
  'IDN-TD.0116' = 'VAT borne by the GOVERNMENT EXECUTION PMK NUMBER 103 / PMK.010 / 2021',
  'IDN-TD.0117' = 'VALUE ADDED TAX IS NOT COLLECTED BASED ON PP NUMBER 40 OF 2021',
  'IDN-TD.0118' = 'VALUE ADDED TAX IS NOT COLLECTED BASED ON PP NUMBER 41 OF 2021',
  'IDN-TD.0119' = 'VAT is borne by the GOVERNMENT OF EX PMK 6 / PMK.010 / 2022',
  'IDN-TD.0120' = 'VAT borne by the GOVERNMENT EXECUTION PMK NUMBER 226 / PMK.03 / 2021',
  'IDN-TD.0121' = 'VAT OR VAT AND STLG WILL NOT BE COLLECTED IN ACCORDANCE WITH PP NUMBER 53 OF 2017',
  'IDN-TD.0122' = 'VAT NOT COLLECTED BASED ON PP NUMBER 70 OF 2021',
  'IDN-TD.0123' = 'VAT borne by the GOVERNMENT EX PMK 125 / PMK.010 / 2020',
  'IDN-TD.0124' = 'no stamp)',
  'IDN-TD.0125' = 'VAT not collected based on PP NUMBER 49 YEAR 2022',
  'IDN-TD.0126' = 'VAT not collected based on PP NUMBER 12 YEAR 2023',
  'IDN-TD.0127' = 'VAT borne by Government based on PMK Number 12 Year 2025',
  'IDN-TD.0128' = 'PPN DITANGGUNG PEMERINTAH EKSEKUSI PMK NOMOR 60 TAHUN 2025',
  'IDN-TD.0129' = 'PPN DITANGGUNG PEMERINTAH BERDASARKAN PMK NOMOR 61 TAHUN 2025',
  'IDN-TD.0130' = 'PPN DITANGGUNG PEMERINTAH BERDASARKAN PMK NOMOR 44 TAHUN 2025'
}

// tax code = IDN-TD.00308
export enum ExemptReasonCodeIDN8 {
  'IDN-TD.00501' = 'Certain Taxable Goods/Services',
  'IDN-TD.00502' = 'Strategic Taxable Goods as stated in Government Regulation Number 12 Year 2001',
  'IDN-TD.00503' = 'Airport Services',
  'IDN-TD.00504' = 'Others',
  'IDN-TD.00505' = 'Certain Strategic Taxable Goods as stated in Government Regulation Number 81 Year 2015',
  'IDN-TD.00506' = 'Deliveries of Certain Port Services for the Activities of Foreign Ships',
  'IDN-TD.00507' = 'Deliveries of Clean Water',
  'IDN-TD.00508' = 'Submission of certain strategic BKP based on PP 48 of 2020',
  'IDN-TD.00509' = 'Submission to Representatives of Foreign Countries and International Agencies and Their Officials',
  'IDN-TD.00510' = 'Certain Taxable Goods and Services',
  'IDN-TD.0101' = 'VAT WAS EXEMPT IN ACCORDANCE with PP NUMBER 146 YEAR 2000 AS AMENDED BY PP NUMBER 38 YEAR 2003',
  'IDN-TD.0102' = 'VAT WAS EXCEPTED ACCORDING TO PP NUMBER 12 OF 2001 AS AMENDED SEVERAL TIMES LAST TO PPN NUMBER 31 OF 2007',
  'IDN-TD.0103' = 'VAT is exempt based on Government Regulation Number 28 of 2009',
  'IDN-TD.0104' = 'no stamp)',
  'IDN-TD.0105' = 'VAT WILL BE FREE IN ACCORDANCE WITH PP NUMBER 81 OF 2015',
  'IDN-TD.0106' = 'VAT WAS EXCEPTED BASED ON PP NUMBER 74 OF 2015)',
  'IDN-TD.0107' = 'no stamp)',
  'IDN-TD.0108' = 'VAT WAS EXCLUDED IN ACCORDANCE WITH PP NUMBER 81 YEAR 2015 AS AMENDED TO PP 48 YEAR 2020',
  'IDN-TD.0109' = 'VAT WAS EXCLUDED BASED ON PP NUMBER 47 YEAR 2020',
  'IDN-TD.0110' = 'VAT WAS EXEMPT IN ACCORDANCE with PP NUMBER 49 YEAR 2022'
}

export enum ExemptReasonCodeEnumITA {
  'N1' = 'excluded pursuant to Art. 15',
  'N2.1' = 'not subject to VAT under the articles from 7 to 7-septies of DPR 633/72',
  'N2.2' = 'not subject – other cases',
  'N3.1' = 'not taxable - exportations',
  'N3.2' = 'not taxable - intra Community transfers',
  'N3.3' = 'not taxable - transfers to San Marino',
  'N3.4' = 'not taxable - transactions treated as export supplies',
  'N3.5' = 'not taxable - for declaration of intent',
  'N3.6' = 'not taxable – other transactions that don’t contribute to the determination of ceiling',
  'N4' = 'exempt',
  'N5' = 'margin regime / VAT not exposed on invoice',
  /* eslint-disable max-len */
  'N6' = 'reverse charge (for transactions in reverse charge or for self invoicing for purchase of extra UE services or for import of goods only in the cases provided for) (this code is no longer permitted to use on invoices emitted from 1 January 2021 )',
  'N6.1' = 'reverse charge - transfer of scrap and of other recyclable materials',
  'N6.2' = 'reverse charge - transfer of gold and pure silver',
  'N6.3' = 'reverse charge - subcontracting in the construction sector',
  'N6.4' = 'reverse charge - transfer of buildings',
  'N6.5' = 'reverse charge - transfer of mobile phones',
  'N6.6' = 'reverse charge - transfer of electronic products',
  'N6.7' = 'reverse charge - provisions in the construction and related sectors',
  'N6.8' = 'reverse charge - transactions in the energy sector',
  'N6.9' = 'reverse charge - other cases',
  /* eslint-disable max-len */
  'N7' = 'VAT paid in other EU countries (telecommunications, tele-broadcasting and electronic services provision pursuant to Art. 7 -sexies letter f, g, art. 74-sexies Italian Presidential Decree 633/72)'
}

export enum ExemptReasonCodeEnumPRTandPEPPOL {
  M01 = 'Article 16, paragraph 6, subparagraphs a) to d) of the CIVA',
  M02 = 'Article 6 of Decree Law No. 198/90 of 19 June',
  M04 = 'Article 13 of the VAT Code',
  M05 = 'Article 14 of the VAT Code',
  M06 = 'Article 15 of the VAT Code',
  M07 = 'Article 9 of the VAT Code',
  M09 = 'Article 62, paragraph b) of the CIVA',
  M10 = 'Article 57 of the VAT Code',
  M11 = 'Decree-Law No. 346/85, of August 23',
  M12 = 'Decree-Law No. 221/85 of 3 July',
  M13 = 'Second-hand goods - Indication that the VAT margin scheme for second-hand goods has been applied.',
  M14 = 'Works of art - Indication that the VAT margin scheme for works of art has been applied.',
  M15 = `Collectors' items and antiques - Indication that the VAT margin scheme for collector's items and antiques has been applied.`,
  M16 = 'Article 14 of the RITI',
  M19 = 'Temporary exemptions determined in a specific diploma',
  M20 = 'Article 59.º-D no. 2 of the CIVA',
  M21 = 'Article 72.4 of the CIVA',
  M25 = 'Article 38.1 a) of the CIVA',
  M26 = 'Law No. 17/2023, of April 14',
  M30 = 'Article 2, paragraph 1, paragraph i) of the CIVA',
  M31 = 'Article 2, paragraph 1, letter j) of the CIVA',
  M32 = 'Article 2, paragraph 1, letter l) of the CIVA',
  M33 = 'Article 2, paragraph 1, letter m) of the CIVA',
  M34 = 'Article 2, paragraph 1, paragraph n) of the CIVA',
  M40 = 'Article 6, paragraph 6, paragraph a) of the CIVA, contrary to',
  M41 = 'Article 8.3 of the RITI',
  M42 = 'Decree-Law No. 21/2007, of January 29',
  M43 = 'Decree-Law 362/99 of 16 September',
  M99 = 'Supports EN 16931-1 rule BR-O-10'
}

export enum ExemptReasonCodeEnumROU {
  'vatex-eu-132' = 'Exemptions for certain activities in public interest.',
  'vatex-eu-132-1a' = 'The supply by the public postal services of services other than passenger transport and telecommunications services, and the supply of goods incidental thereto.',
  'vatex-eu-132-1b' = 'Hospital and medical care and closely related activities undertaken by bodies governed by public law or, under social conditions comparable with those applicable to bodies governed by public law, by hospitals, centres for medical treatment or diagnosis and other duly recognised establishments of a similar nature',
  'vatex-eu-132-1c' = 'The provision of medical care in the exercise of the medical and paramedical professions as defined by the Member State concerned.',
  'vatex-eu-132-1d' = 'The supply of human organs, blood and milk.',
  'vatex-eu-132-1e' = 'The supply of services by dental technicians in their professional capacity and the supply of dental prostheses by dentists and dental technicians.',
  'vatex-eu-132-1f' = 'The supply of services by independent groups of persons, who are carrying on an activity which is exempt from VAT or in relation to which they are not taxable persons, for the purpose of rendering their members the services directly necessary for the exercise of that activity, where those groups merely claim from their members exact reimbursement of their share of the joint expenses, provided that such exemption is not likely to cause distortion of competition.',
  'vatex-eu-132-1g' = `The supply of services and of goods closely linked to welfare and social security work, including those supplied by old people's homes, by bodies governed by public law or by other bodies recognised by the Member State concerned as being devoted to social wellbeing.`,
  'vatex-eu-132-1h' = 'The supply of services and of goods closely linked to the protection of children and young persons by bodies governed by public law or by other organisations recognised by the Member State concerned as being devoted to social wellbeing;',
  'vatex-eu-132-1i' = `The provision of children's or young people's education, school or university education, vocational training or retraining, including the supply of services and of goods closely related thereto, by bodies governed by public law having such as their aim or by other organisations recognised by the Member State concerned as having similar objects.`,
  'vatex-eu-132-1j' = 'Tuition given privately by teachers and covering school or university education.',
  'vatex-eu-132-1k' = 'The supply of staff by religious or philosophical institutions for the purpose of the activities referred to in points (b), (g), (h) and (i) and with a view to spiritual welfare.',
  'vatex-eu-132-1l' = 'The supply of services, and the supply of goods closely linked thereto, to their members in their common interest in return for a subscription fixed in accordance with their rules by non-profitmaking organisations with aims of a political, trade-union, religious, patriotic, philosophical, philanthropic or civic nature, provided that this exemption is not likely to cause distortion of competition.',
  'vatex-eu-132-1m' = 'The supply of certain services closely linked to sport or physical education by non-profit-making organisations to persons taking part in sport or physical education.',
  'vatex-eu-132-1n' = 'The supply of certain cultural services, and the supply of goods closely linked thereto, by bodies governed by public law or by other cultural bodies recognised by the Member State concerned.',
  'vatex-eu-132-1o' = 'The supply of services and goods, by organisations whose activities are exempt pursuant to points (b), (g), (h), (i), (l), (m) and (n), in connection with fund-raising events organised exclusively for their own benefit, provided that exemption is not likely to cause distortion of competition.',
  'vatex-eu-132-1p' = 'The supply of transport services for sick or injured persons in vehicles specially designed for the purpose, by duly authorised bodies.',
  'vatex-eu-132-1q' = 'The activities, other than those of a commercial nature, carried out by public radio and television bodies.',
  'vatex-eu-143' = 'Exemptions on importation.',
  'vatex-eu-143-1a' = 'The final importation of goods of which the supply by a taxable person would in all circumstances be exempt within their respective territory.',
  'vatex-eu-143-1b' = 'The final importation of goods governed by Council Directives 69/169/EEC (1), 83/181/EEC (2) and 2006/79/EC (3).',
  'vatex-eu-143-1c' = 'The final importation of goods, in free circulation from a third territory forming part of the Community customs territory, which would be entitled to exemption under point (b) if they had been imported within the meaning of the first paragraph of Article 30',
  'vatex-eu-143-1d' = 'The importation of goods dispatched or transported from a third territory or a third country into a Member State other than that in which the dispatch or transport of the goods ends, where the supply of such goods by the importer designated or recognised under Article 201 as liable for payment of VAT is exempt under Article 138.',
  'vatex-eu-143-1e' = 'The reimportation, by the person who exported them, of goods in the state in which they were exported, where those goods are exempt from customs duties.',
  'vatex-eu-143-1f' = 'The importation, under diplomatic and consular arrangements, of goods which are exempt from customs duties.',
  'vatex-eu-143-1fa' = 'The importation of goods by the European Community, the European Atomic Energy Community, the European Central Bank or the European Investment Bank, or by the bodies set up by the Communities to which the Protocol of 8 April 1965 on the privileges and immunities of the European Communities applies, within the limits and under the conditions of that Protocol and the agreements for its implementation or the headquarters agreements, in so far as it does not lead to distortion of competition;',
  'vatex-eu-143-1g' = 'The importation of goods by international bodies, other than those referred to in point (fa), recognised as such by the public authorities of the host Member State, or by members of such bodies, within the limits and under the conditions laid down by the international conventions establishing the bodies or by headquarters agreements;',
  'vatex-eu-143-1h' = 'The importation of goods, into Member States party to the North Atlantic Treaty, by the armed forces of other States party to that Treaty for the use of those forces or the civilian staff accompanying them or for supplying their messes or canteens where such forces take part in the common defence effort.',
  'vatex-eu-143-1i' = 'The importation of goods by the armed forces of the United Kingdom stationed in the island of Cyprus pursuant to the Treaty of Establishment concerning the Republic of Cyprus, dated 16 August 1960, which are for the use of those forces or the civilian staff accompanying them or for supplying their messes or canteens.',
  'vatex-eu-143-1j' = 'The importation into ports, by sea fishing undertakings, of their catches, unprocessed or after undergoing preservation for marketing but before being supplied.',
  'vatex-eu-143-1k' = 'The importation of gold by central banks.',
  'vatex-eu-143-1l' = 'The importation of gas through a natural gas system or any network connected to such a system or fed in from a vessel transporting gas into a natural gas system or any upstream pipeline network, of electricity or of heat or cooling energy through heating or cooling networks.',
  'vatex-eu-148' = 'Exemptions related to international transport.',
  'vatex-eu-148-a' = 'Fuel supplies for commercial international transport vessels',
  'vatex-eu-148-b' = 'Fuel supplies for fighting ships in international transport.',
  'vatex-eu-148-c' = 'Maintenance, modification, chartering and hiring of international transport vessels.',
  'vatex-eu-148-d' = 'Supply to of other services to commercial international transport vessels.',
  'vatex-eu-148-e' = 'Fuel supplies for aircraft on international routes.',
  'vatex-eu-148-f' = 'Maintenance, modification, chartering and hiring of aircraft on international routes.',
  'vatex-eu-148-g' = 'Supply to of other services to aircraft on international routes.',
  'vatex-eu-151' = 'Exemptions relating to certain Transactions treated as exports.',
  'vatex-eu-151-1a' = 'The supply of goods or services under diplomatic and consular arrangements.',
  'vatex-eu-151-1aa' = 'The supply of goods or services to the European Community, the European Atomic Energy Community, the European Central Bank or the European Investment Bank, or to the bodies set up by the Communities to which the Protocol of 8 April 1965 on the privileges and immunities of the European Communities applies, within the limits and under the conditions of that Protocol and the agreements for its implementation or the headquarters agreements, in so far as it does not lead to distortion of competition.',
  'vatex-eu-151-1b' = 'The supply of goods or services to international bodies, other than those referred to in point (aa), recognised as such by the public authorities of the host Member States, and to members of such bodies, within the limits and under the conditions laid down by the international conventions establishing the bodies or by headquarters agreements.',
  'vatex-eu-151-1c' = 'The supply of goods or services within a Member State which is a party to the North Atlantic Treaty, intended either for the armed forces of other States party to that Treaty for the use of those forces, or of the civilian staff accompanying them, or for supplying their messes or canteens when such forces take part in the common defence effort.',
  'vatex-eu-151-1d' = 'The supply of goods or services to another Member State, intended for the armed forces of any State which is a party to the North Atlantic Treaty, other than the Member State of destination itself, for the use of those forces, or of the civilian staff accompanying them, or for supplying their messes or canteens when such forces take part in the common defence effort.',
  'vatex-eu-151-1e' = 'The supply of goods or services to the armed forces of the United Kingdom stationed in the island of Cyprus pursuant to the Treaty of Establishment concerning the Republic of Cyprus, dated 16 August 1960, which are for the use of those forces, or of the civilian staff accompanying them, or for supplying their messes or canteens.',
  'vatex-eu-309' = 'Travel agents performed outside of EU.',
  'vatex-eu-ae' = 'Supports EN 16931-1 rule BR-AE-10',
  'vatex-eu-d' = 'Second-hand means of transport - Indication that VAT has been paid according to the relevant transitional arrangements',
  'vatex-eu-f' = 'Second-hand goods - Indication that the VAT margin scheme for second-hand goods has been applied.',
  'vatex-eu-g' = 'Supports EN 16931-1 rule BR-G-10',
  'vatex-eu-i' = 'Works of art - Indication that the VAT margin scheme for works of art has been applied.',
  'vatex-eu-ic' = 'Supports EN 16931-1 rule BR-IC-10',
  'vatex-eu-j' = `Collectors' items and antiques - Indication that the VAT margin scheme for collector's items and antiques has been applied.`,
  'vatex-eu-o' = 'Supports EN 16931-1 rule BR-O-10',
  'VATEX-EU-144' = 'Exemptions for services linked to the import of goods',
  'VATEX-EU-146-1E' = 'Exemptions for services linked to the export of goods',
  'VATEX-EU-159' = 'Exemptions for services linked to supplies of goods intended to be placed under customs warehouses, warehouses other than customs warehouses and similar arrangements.'
}

// ARE exempt reason codes when VAT code is E (Exempt)
export enum ExemptReasonCodeEnumAREExempt {
  'DL8.46.1' = 'Certain financial services',
  'DL8.46.2' = 'Supply of residential units (lease or sale)',
  'DL8.46.3' = 'Bare land',
  'DL8.46.4' = 'Local passenger transport'
}

// ARE exempt reason codes when VAT code is AE (Reverse charge)
export enum ExemptReasonCodeEnumAREReverseCharge {
  'DL8.48.8.2' = 'Electronic Devices',
  'DL8.48.8.1' = 'Gold and Diamonds',
  'DL8.48.3.1' = 'Crude or refined oil',
  'DL8.48.3.2' = 'Unprocessed or processed natural gas',
  'DL8.48.3.3' = 'Pure hydrocarbons'
}

export const defaultExemptReasonCodeAREReverseCharge = 'DL8.48.8.2';

export enum ExemptReasonCodeEnumSAU {
  'VATEX-SA-29' = 'Financial services mentioned in Article 29 of the VAT Regulations.',
  'VATEX-SA-29-7' = 'Life insurance services mentioned in Article 29 of the VAT Regulations.',
  'VATEX-SA-30' = 'Real estate transactions mentioned in Article 30 of the VAT Regulations.',
  'VATEX-SA-32' = 'Export of goods.',
  'VATEX-SA-33' = 'Export of services.',
  // Business rules
  'BR-E01' = 'An invoice that contains an invoice line (BG-25), a Document level allowance (BG-20) or Document level charge (BG-21) where the VAT category (BT-151 or BT-95 or BT-102) is “Exempt from VAT” shall contain at least one VAT breakdown (BG-23) with the VAT category code (BT-118) equal to "Exempt from VAT".',
  'BR-E05' = 'In an invoice line (BG-25) where the invoiced item VAT category code (BT-151) is “Exempt from VAT”, the invoices item VAT rate (BT-152) shall be 0 (zero)',
  'BR-E-06' = 'In a Document level allowance (BG-20) where the Document level allowance VAT category code (BT-95) is “Exempt from VAT”, the Document level allowance VAT rate (BT-96) shall be zero (0).',
  'BR-E-07' = 'In a Document level charge (BG-21) where the Document level charge VAT category code (BT-102) is “Exempt from VAT”, the Document level charge VAT rate (BT-103) shall be zero (0).',
  'BR-E-08' = 'In each line VAT breakdown (BG-23) where the VAT category code (BT-118) is “Exempt from VAT”, the VAT category taxable amount (BT-116) shall equal the sum of the invoice line net amount (BT_131) minus the sim of the Document level allowance amount (BT-92) plus the sum of the Document level charge amount (BT-99) where the VAT category code (BT-151, BT-95, BT-102) are "Exempt from VAT".',
  'BR-E-09' = 'The VAT category tax amount (BT-117) in a VAT breakdown (BG-23) where the VAT category code (BT-118) equals "Exempt from VAT" shall equal 0(zero)'
}

export enum IncomeClassificationCategoryCodeGRC {
  'CATEGORY1_1' = 'Commodity Sale Income',
  'CATEGORY1_2' = 'Product Sale Income',
  'CATEGORY1_3' = 'Provision of Services Income',
  'CATEGORY1_4' = 'Sale of Fixed Assets Income',
  'CATEGORY1_5' = 'Other Income/Profits',
  'CATEGORY1_6' = 'Self-Deliveries/Self-Supplies',
  'CATEGORY1_7' = 'Income on behalf of Third Parties',
  'CATEGORY1_8' = 'Past fiscal years income',
  'CATEGORY1_9' = 'Future fiscal years income',
  'CATEGORY1_10' = 'Other Income Adjustment/Regularisation Entries',
  'CATEGORY1_95' = 'Other Income-related Information',
  'CATEGORY2_1' = 'Commodity Purchases',
  'CATEGORY2_2' = 'Raw and Adjuvant Material Purchases',
  'CATEGORY2_3' = 'Services Receipt',
  'CATEGORY2_4' = 'General Expenses Subject to VAT Deduction',
  'CATEGORY2_5' = 'General Expenses Not Subject to VAT Deduction',
  'CATEGORY2_6' = 'Personnel Fees and Benefits',
  'CATEGORY2_7' = 'Fixed Asset Purchases',
  'CATEGORY2_8' = 'Fixed Asset Amortisations',
  'CATEGORY2_9' = 'Expenses on behalf of Third Parties',
  'CATEGORY2_10' = 'Past fiscal years expenses',
  'CATEGORY2_11' = 'Future fiscal years expenses',
  'CATEGORY2_12' = 'Other Expense Adjustment/Regularisation Entries',
  'CATEGORY2_13' = 'Stock at Period Start',
  'CATEGORY2_14' = 'Stock at Period End',
  'CATEGORY2_95' = 'Other Expense-related Information'
}

export enum IncomeClassificationTypeCodeGRC {
  'E3_106' = 'Self-Production of Fixed Assets - Self-Deliveries - Destroying inventory/Commodities',
  'E3_205' = 'Self-Production of Fixed Assets - Self-Deliveries - Destroying inventory/Raw and other materials',
  'E3_210' = 'Self-Production of Fixed Assets - Self-Deliveries - Destroying inventory/Products and production in progress',
  'E3_305' = 'Self-Production of Fixed Assets - Self-Deliveries - Destroying inventory/Raw and other materials',
  'E3_310' = 'Self-Production of Fixed Assets - Self-Deliveries - Destroying inventory/ Products and production in progress',
  'E3_318' = 'Self-Production of Fixed Assets - Self-Deliveries - Destroying inventory/Production expenses',
  'E3_561_001' = 'Wholesale Sales of Goods and Services - for Traders',
  'E3_561_002' = 'Wholesale Sales of Goods and Services pursuant to article 39a paragraph 5 of the VAT Code (Law 2859/2000)',
  'E3_561_003' = 'Retail Sales of Goods and Services - Private Clientele',
  'E3_561_004' = 'Retail Sales of Goods and Services pursuant to article 39a paragraph 5 of the VAT Code (Law 2859/2000)',
  'E3_561_005' = 'Intra-Community Foreign Sales of Goods and Services',
  'E3_561_006' = 'Third Country Foreign Sales of Goods and Services',
  'E3_561_007' = 'Other Sales of Goods and Services',
  'E3_562' = 'Other Ordinary Income',
  'E3_563' = 'Credit Interest and Related Income',
  'E3_564' = 'Credit Exchange Differences',
  'E3_565' = 'Income from Participations',
  'E3_566' = 'Profits from Disposing Non-Current Assets',
  'E3_567' = 'Profits from the Reversal of Provisions and Impairments',
  'E3_568' = 'Profits from Measurement at Fair Value',
  'E3_570' = 'Extraordinary income and profits',
  'E3_595' = 'Self-Production Expenses',
  'E3_596' = 'Subsidies - Grants',
  'E3_597' = 'Subsidies - Grants for Investment Purposes - Expense Coverage',
  'E3_880_001' = 'Wholesale Sales of Fixed Assets',
  'E3_880_002' = 'Retail Sales of Fixed Assets',
  'E3_880_003' = 'Intra-Community Foreign Sales of Fixed Assets',
  'E3_880_004' = 'Third Country Foreign Sales of Fixed Assets',
  'E3_881_001' = 'Wholesale Sales on behalf of Third Parties',
  'E3_881_002' = 'Retail Sales on behalf of Third Parties',
  'E3_881_003' = 'Intra-Community Foreign Sales on behalf of Third Parties',
  'E3_881_004' = 'Third Country Foreign Sales on behalf of Third Parties'
}

export enum InvoiceTypeESPSII {
  'F1' = 'Regular',
  'F2' = 'Simplified'
  // 'F3' = 'Invoice issued to replace simplified invoices',
  // 'F4' = 'Summary invoice (Factura resumen)',
  // 'F5' = 'Import invoice',
  // 'F6' = 'Accounting entry for imports (Asiento contable)',
}

export enum InvoiceCorrectionTypeESPSII {
  'R1' = 'Error in legal data',
  'R2' = 'Error in amounts',
  'R3' = 'Returned goods',
  'R4' = 'Discounts / bonuses',
  'R5' = 'Bad debts / insolvency'
}

export enum PaymentMethodCodeJOR {
  // @ts-ignore
  '1' = 'Instrument not defined',
  // @ts-ignore
  '10' = ' In cash',
  // @ts-ignore
  '30' = 'Credit Transfer',
  // @ts-ignore
  '42' = 'Payment to bank account',
  // @ts-ignore
  '48' = 'Bank card',
  // @ts-ignore
  'ZZZ' = 'Mutually defined'
}

export enum PaymentMethodCodeKEN {
  'KEN-CASH' = 'Cash', // 01
  'KEN-CREDIT' = 'Credit', // 02
  'KEN-CASH/CREDIT' = 'Cash/Credit', // 03
  'KEN-CHECK' = 'Check', // 04
  'KEN-CARD' = 'Card', // 05
  'KEN-MOBILE_MONEY' = 'Mobile money', // 06
  'KEN-OTHER' = 'Other' // 07
}

export const PaymentMeansCodesPOL = {
  '1': {
    name: 'Payment by cash',
    desc: 'Payment by cash'
  },
  '2': {
    name: 'Payment by card',
    desc: 'Payment by card'
  },
  '3': {
    name: 'Payment by voucher',
    desc: 'Payment by voucher'
  },
  '4': {
    name: 'Payment by cheque',
    desc: 'Payment by cheque'
  },
  '5': {
    name: 'Credit',
    desc: 'Credit'
  },
  '6': {
    name: 'Payment by bank transfer',
    desc: 'Payment by bank transfer'
  },
  '7': {
    name: 'Payment using mobile phone',
    desc: 'Payment using mobile phone'
  }
};

export enum PurposeTypeKOR {
  '영수' = '영수 (Payment received)',
  '청구' = '청구 (Payment not received)',
  '없음' = '없음 (None)',
  'R' = 'R - Payment received',
  'C' = 'C - Payment not received',
  'N' = 'N - None',
  'Receipt' = 'Payment received',
  'Charge' = 'Payment not received',
  'None' = 'None',
  'Payment Received' = 'Payment received',
  'Payment not received' = 'Payment not received'
}
export const PurposeTypeOptionsKOR = ['Receipt', 'Charge', 'None'] as const;
export const normalizePurposeTypeKOR = (raw?: string | number | null): PurposeTypeKOR | '' => {
  if (raw === undefined || raw === null || raw === '') return '';
  return PurposeTypeKOR[String(raw) as keyof typeof PurposeTypeKOR] ?? '';
};

export enum CreditReasonCodeARE {
  'DL8.61.1.A' = 'If the supply was cancelled.',
  'DL8.61.1.B' = 'If the tax treatment of the supply has changed due to a change in the nature of the supply.',
  'DL8.61.1.C' = 'If the previously agreed consideration for the supply was altered for any reason (i.e. bad debt relief).',
  'DL8.61.1.D' = 'If the recipient of goods or recipient of services returned them to the registrant in full or in part and the Consideration was returned in full or in part.',
  'DL8.61.1.E' = 'If the tax was charged or tax treatment was applied in error.',
  'VD' = 'Volume Discount.'
}

export enum ReasonCodeKOR {
  // @ts-ignore
  '2' = 'Changes in Supply Cost',
  // @ts-ignore
  '3' = 'Return',
  // @ts-ignore
  '4' = 'Cancellation of Contract',
  // @ts-ignore
  '6' = 'Duplicated Issuance'
}

export const RecipientVatStatusHUN = ['DOMESTIC', 'OTHER', 'PRIVATE_PERSON'];

export const RoleTypeEnum = {
  '1': 'Factor', // when the invoice includes the factor’s details',
  '2': 'Recipient', // when the invoice includes internal units, branches, or divisions of the purchaser, which do not themselves qualify as purchasers under the Act',
  '3': 'Original entity', // when the invoice includes the details of an entity that was taken over or transformed and originally supplied the goods or services (excluding cases referred to in Article 106j sec. 2 item 3 of the Act, where these details are shown in section Podmiot1K)',
  '4': 'Additional purchaser', // when the invoice includes details of further purchasers (other than the one indicated in section Podmiot2)',
  '5': 'Invoice issuer', // when the invoice includes details of the entity issuing the invoice on behalf of the taxpayer',
  '6': 'Payer', // when the invoice includes details of the entity making the payment on behalf of the purchaser',
  '7': 'Local government unit (LGU) - issuer',
  '8': 'Local government unit (LGU) - recipient',
  '9': 'GV member - issuer',
  '10': 'GV member - recipient',
  '11': 'Employee' // If the invoice includes the details of a third party with a role other than those listed above, the Rola field should be omitted. In such cases, the fields RolaInna and OpisRoli must be completed.',
};

export enum ScenarioIDsPAK {
  'SN001' = 'Standard for registered buyer',
  'SN002' = 'standard for unregistered buyer',
  'SN005' = 'Reduced rate goods',
  'SN006' = 'Exempt goods',
  'SN007' = 'Zero-rated goods',
  'SN026' = 'Standard rate to end consumers by retailers',
  'SN028' = 'Goods at reduced rate to end consumers by retailers'
}

export enum SupplyTypeIND {
  'B2B' = 'Business to Business',
  'B2C' = 'Business to Consumer',
  'EXPWP' = 'Export with Payment of Tax',
  'EXPWOP' = 'Export without Payment of Tax',
  'SEZWP' = 'Supply to SEZ with Payment of Tax',
  'SEZWOP' = 'Supply to SEZ without Payment of Tax',
  'DEXP' = 'Deemed Export'
}

export enum TaskTypeESPSII {
  'A0' = 'Invoice/record entry',
  'A1' = 'Modification of invoices/records (registry errors)',
  'A4' = 'Modification of Travelers Invoice',
  'A5' = 'Registration of VAT refunds for travelers',
  'A6' = 'Modification of VAT refunds for travelers'
}

export enum TaxIdTypeESPSII {
  '02' = 'VAT-NIF',
  '03' = 'Passport',
  '04' = 'Official identification document issued by the country or territory of residence',
  '05' = 'Certificate of residence',
  '06' = 'Other supporting document',
  '07' = 'Not registered'
}

export enum TransactionTypeEnumFRA {
  'B2B' = 'Business to Business',
  'B2C' = 'Business to Consumer'
}

export enum ProfileTypeEnumFRA {
  'B1' = 'Filing of a goods invoice',
  'S1' = 'Filing of a service invoice',
  'M1' = 'Filing of a double invoice (delivery of goods and services that are not ancillary)',
  'B2' = 'Filing of a goods invoice that has already been paid',
  'S2' = 'Filing of a service invoice that has already been paid',
  'M2' = 'Filing of a double invoice that has already been paid',
  'S3' = 'Filing of a subcontract payment request with direct payment (B2G only, restriction not verifiable)',
  'B4' = 'Filing of a final invoice (after advance payment) for goods',
  'S4' = 'Filing of a final invoice (after advance payment) for services',
  'M4' = 'Filing of a final invoice (after advance payment) for double services',
  'S5' = 'Filing of a service invoice by a subcontractor',
  'S6' = 'Filing by a subcontractor of a service invoice',
  'B7' = 'Filing of a goods invoice that has been e-reported (VAT already collected)',
  'S7' = 'Filing of a service invoice that has been e-reported (VAT already collected)',
  'B8' = 'Filing of a multi-vendor goods invoice',
  'S8' = 'Filing of a multi-vendor service invoice',
  'M8' = 'Filing of a double multi-vendor invoice, containing individual invoices that are not all Sx or Bx.',
  'B9' = 'Filing of a bidirectional goods invoice',
  'S9' = 'Filing of a bidirectional service invoice',
  'M9' = 'Filing of a double bidirectional invoice'
}

export enum TaxIdTypesJOR {
  NAT = 'National Number (code: 1)',
  IQA = 'Iqama ID (code: 2)',
  PAS = 'Passport Number (code: 3)',
  CRN = 'Commercial Registration (code: 4)',
  MOM = 'MOMRA license (code: 5)',
  MLS = 'MLSD license (code: 6)',
  SAG = 'SAGIA license (code: 7)',
  GCC = 'Gulf Cooperation Council (code: 8)',
  OTH = 'Other ID (code: 9)',
  TIN = 'Taxpayer Identification Number (code: 10)'
}

export enum TaxNumberTypeESPVeriFactu {
  '02' = 'VAT-NIF',
  '03' = 'Passport',
  '04' = 'Official identification document issued by the country or territory of residence',
  '05' = 'Certificate of residence',
  '06' = 'Other supporting document',
  '07' = 'Not registered'
}
// 사업자 OR Company OR 1; 개인 OR Individual OR 2;  외국인 OR Foreigner OR 3
export enum TaxRegTypeKOR {
  // @ts-ignore
  '1' = 'Company',
  '사업자' = 'Company',
  'Company' = 'Company',
  // @ts-ignore
  '2' = 'Individual',
  '개인' = 'Individual',
  'Individual' = 'Individual',
  // @ts-ignore
  '3' = 'Foreigner',
  '외국인' = 'Foreigner',
  'Foreigner' = 'Foreigner'
}
export const TaxRegTypeOptionsKOR = ['Company', 'Individual', 'Foreigner'] as const;
export const normalizeTaxRegTypeKOR = (raw?: string | number | null): TaxRegTypeKOR | '' => {
  if (raw === undefined || raw === null || raw === '') return '';
  return TaxRegTypeKOR[String(raw) as keyof typeof TaxRegTypeKOR] ?? '';
};

export const defaultTaxTypes = ['S', 'Z', 'E', 'AE', 'G', 'O', 'K', 'AA', 'A', 'H'];

export const defaultTaxExemptCodes = ['EXEMPT', 'E', 'OUTSIDE_SCOPE', 'O', 'ZERO_RATED', 'Z'];

export const TaxTypeGRC = {
  // Standard types
  S: {
    name: 'Standard',
    desc: 'Standard'
  },
  Z: {
    name: 'Zero rated',
    desc: 'Zero rated'
  },
  E: {
    name: 'Exempt',
    desc: 'Exempt'
  },
  AE: {
    name: 'Reverse charge',
    desc: 'Reverse charge'
  },
  G: {
    name: 'Free export',
    desc: 'Free export item, tax not charged'
  },
  O: {
    name: 'Outside of scope',
    desc: 'Tax-exempt/Out of scope services'
  },
  K: {
    name: 'Intra-community supply',
    desc: 'VAT exempt for EEA intra-community supply of goods and services'
  },
  AA: {
    name: 'Reduced',
    desc: 'Lower rate'
  },
  A: {
    name: 'Mixed',
    desc: 'Mixed rate'
  },
  H: {
    name: 'Higher',
    desc: 'Higher rate'
  },
  // Additional types for With holding
  W1: {
    name: "W1 - Case b' - Interests- 15%"
  },
  W2: {
    name: "W2 - Case c'- Royalties - 20%"
  },
  W3: {
    name: "W3 - Case d'- Management Consultant Fees - 20%"
  },
  W4: {
    name: "W4 - Case d'- Technical Projects - 3%"
  },
  W5: {
    name: 'W5 - Liquid fuel and tobacco industry products 1%'
  },
  W6: {
    name: 'W6 - Other Goods 4%'
  },
  W7: {
    name: 'W7 - Service Provision 8%'
  },
  W8: {
    name: 'W8 - 4% Architect and Engineer Tax on Contractual Fees to Draft Surveys and Plans, Payable in Advance'
  },
  W9: {
    name: 'W9 - 10% Architect and Engineer Tax on Contractual Fees for any other Project, Payable in Advance'
  },
  W10: {
    name: 'W10 - 15% Attorney Fee Tax, Payable in Advance'
  },
  W11: {
    name: 'W11 - Payroll Tax Withholding (Article 15 paragraph 1 of Law 4172/2013)'
  },
  W12: {
    name: 'W12 - Merchant Marine Officers Payroll Tax Withholding (article 15 paragraph 2 of Law 4172/2013)'
  },
  W13: {
    name: 'W13 - Merchant Marine Lower-ranking Crew Payroll Tax Withholding (article 15 paragraph 2 of Law 4172/2013)'
  },
  W14: {
    name: 'W14 - Special Solidarity Contribution Withholding'
  },
  W15: {
    name: 'W15 - Compensation for Termination of Employment Tax Withholding (Article 15 paragraph 3 of Law 4172/2013)'
  },
  W16: {
    name: 'W16 - Withholding of foreign transactions based on avoidance agreements double taxation'
  },
  W17: {
    name: 'W17 - Other Tax deduction'
  },
  W18: {
    name: 'W18 - Withholding Tax on Dividends para.a par. 1 no. 4172/2013 5%'
  },
  // Additional types for stamp duty category
  S1: {
    name: 'S1 - Rate 1.2%'
  },
  S2: {
    name: 'S2 - Rate 2.4%'
  },
  S3: {
    name: 'S3 - Rate 3.6%'
  },
  S4: {
    name: 'S4 - Charges of other Taxes'
  }
};

export const TaxTypeEnumIDN = {
  'IDN-TD.00301': 'To other party than VAT Collector',
  'IDN-TD.00302': 'To Government Institution as VAT Collector',
  'IDN-TD.00303': 'To VAT Collector other than Government Institution',
  'IDN-TD.00304': 'Other Tax Base',
  'IDN-TD.00305': 'Certain value Tax Base (use by specific Taxpayer)',
  'IDN-TD.00306': 'To Tourist for VAT Refund for Tourist',
  'IDN-TD.00307': 'VAT Uncollected',
  'IDN-TD.00308': 'VAT Exempted',
  'IDN-TD.00309': 'Sale of unrelated to business asset',
  'IDN-TD.00310': 'Other than normal rate'
};

export enum TaxTypeKOR {
  '과세' = '과세 (Taxable)',
  '영세' = '영세 (Zero-rated)',
  '면세' = '면세 (Exempt)',
  'S' = 'Standard',
  'Z' = 'Zero rated',
  'E' = 'Exempt'
}
export const TaxTypeOptionsKOR = ['Standard', 'Zero rated', 'Exempt'] as const;
export const normalizeTaxTypeKOR = (raw?: string | number | null): TaxTypeKOR | '' => {
  if (raw === undefined || raw === null || raw === '') return '';
  return TaxTypeKOR[String(raw) as keyof typeof TaxTypeKOR] ?? '';
};

export const TaxTypeUNTID5305 = {
  S: {
    name: 'Standard',
    desc: 'Standard'
  },
  Z: {
    name: 'Zero rated',
    desc: 'Zero rated'
  },
  E: {
    name: 'Exempt',
    desc: 'Exempt'
  },
  AE: {
    name: 'Reverse charge',
    desc: 'Reverse charge'
  },
  G: {
    name: 'Free export',
    desc: 'Free export item, tax not charged'
  },
  O: {
    name: 'Outside of scope',
    desc: 'Tax-exempt/Out of scope services'
  },
  // '': {
  //   name: 'Fee',
  //   desc: 'Fee (set applied to tax base)'
  // },
  K: {
    name: 'Intra-community supply',
    desc: 'VAT exempt for EEA intra-community supply of goods and services'
  },
  L: {
    name: 'Canary Islands (IGIC)',
    desc: 'Canary Islands general indirect tax'
  },
  M: {
    name: 'Ceuta and Melilla (IPSI)',
    desc: 'Tax for production, services and importation in Ceuta and Melilla'
  },
  AA: {
    name: 'Reduced',
    desc: 'Lower rate'
  },
  A: {
    name: 'Mixed',
    desc: 'Mixed rate'
  },
  B: {
    name: 'Transferred (VAT)',
    desc: 'Transferred (VAT), In Italy'
  },
  AB: {
    name: 'Exempt for resale',
    desc: 'Exempt for resale'
  },
  AC: {
    name: 'VAT not due for payment',
    desc: 'Value Added Tax (VAT) not now due for payment'
  },
  AD: {
    name: 'VAT due from a previous invoice',
    desc: 'Value Added Tax (VAT) due from a previous invoice'
  },
  C: {
    name: 'Duty paid',
    desc: 'Duty paid by supplier'
  },
  D: {
    name: 'Margin Scheme - travel agents',
    desc: 'Value Added Tax (VAT) margin scheme - travel agents'
  },
  F: {
    name: 'Margin Scheme - second-hand goods',
    desc: 'Value Added Tax (VAT) margin scheme - second-hand goods'
  },
  H: {
    name: 'Higher',
    desc: 'Higher rate'
  },
  I: {
    name: 'Margin Scheme - works of art',
    desc: 'Value Added Tax (VAT) margin scheme - works of art'
  },
  J: {
    name: `Margin Scheme - collector's items and antiques`,
    desc: `Value Added Tax (VAT) margin scheme - collector's items and antiques`
  }
};

export enum TransactionTypeEnumMUS {
  'B2B' = 'Business to Business',
  'B2G' = 'Business to Government',
  'B2C' = 'Business to Consumer'
}

export enum PersonTypeEnumMUS {
  'VATR' = 'VAT Registered Person',
  'NVTR' = 'Non VAT Registered Person'
}

export enum InvoiceTypeEnumMUS {
  'ST' = 'Standard',
  'PRF' = 'Proforma',
  'TRN' = 'Training',
  'CRN' = 'Credit',
  'DRN' = 'Debit'
}

export enum TaxTypeEnumMUS {
  'TC01' = 'Standard',
  'TC02' = 'Zero rated',
  'TC03' = 'Exempt'
}

export enum GoodsOrServiceEnumMUS {
  'GOODS' = 'Goods',
  'SERVICES' = 'Service'
}

export enum SalesTransactionMUS {
  'CASH' = 'Cash',
  'BNKTRANSFER' = 'Bank transfer',
  'CHEQUE' = 'Cheque',
  'CARD' = 'Card',
  'CREDIT' = 'Credit'
}

export enum BuyerTypeEnumMUS {
  'VATR' = 'VAT Registered Person',
  'NVTR' = 'Non VAT Registered Person',
  'EXMP' = 'Exempt'
}

export enum TaxTypeEnumMYS {
  '01' = 'Sales Tax',
  '02' = 'Service Tax',
  '03' = 'Tourism Tax',
  '04' = 'High-Value Goods Tax',
  '05' = 'Sales Tax on Low Value Goods',
  '06' = 'Outside of scope',
  'E' = 'Exempt'
}

export enum IdentificationTypeEnumMYS {
  'BRN' = 'Business registration number',
  'ARMY' = 'Malaysian individual: MyKad / MyTentera identification number',
  'Passport' = 'non-Malaysian individual: Passport number / MyPR / MyKAS identification number',
  'NRIC' = 'National Registration Identification Card'
}

export enum RecipientPartyTypeEnumEGY {
  'Business' = 'Business',
  'Consumer' = 'Consumer',
  'Foreign' = 'Foreign'
}

export const TaxTypeDNK = [
  'Standard',
  'Zero rated',
  'Reverse charge',
  'Excise',
  '3010',
  '3020',
  '3021',
  '3022',
  '3023',
  '3024',
  '3025',
  '3030',
  '3040',
  '3041',
  '3048',
  '3049',
  '3050',
  '3051',
  '3052',
  '3053',
  '3054',
  '3055',
  '3056',
  '3057',
  '3058',
  '3059',
  '3060',
  '3061',
  '3062',
  '3063',
  '3064',
  '3065',
  '3066',
  '3067',
  '3068',
  '3070',
  '3071',
  '3072',
  '3073',
  '3075',
  '3080',
  '3081',
  '3082',
  '3083',
  '3084',
  '3085',
  '3086',
  '3090',
  '3091',
  '3092',
  '3093',
  '3094',
  '3095',
  '3096',
  '3100',
  '3101',
  '3102',
  '3120',
  '3121',
  '3122',
  '3123',
  '3130',
  '3140',
  '3141',
  '3160',
  '3161',
  '3162',
  '3163',
  '3170',
  '3171',
  '3240',
  '3241',
  '3242',
  '3245',
  '3246',
  '3247',
  '3250',
  '3251',
  '3260',
  '3271',
  '3272',
  '3273',
  '3276',
  '3277',
  '3280',
  '3281',
  '3282',
  '3283',
  '3290',
  '3291',
  '3292',
  '3293',
  '3294',
  '3295',
  '3296',
  '3297',
  '3300',
  '3301',
  '3302',
  '3303',
  '3304',
  '3305',
  '3310',
  '3311',
  '3320',
  '3321',
  '3330',
  '3331',
  '3340',
  '3341',
  '3350',
  '3351',
  '3360',
  '3370',
  '3380',
  '3400',
  '3403',
  '3404',
  '3405',
  '3406',
  '3410',
  '3420',
  '3430',
  '3440',
  '3441',
  '3451',
  '3452',
  '3453',
  '3500',
  '3501',
  '3502',
  '3503',
  '3600',
  '3620',
  '3621',
  '3622',
  '3623',
  '3624',
  '3630',
  '3631',
  '3632',
  '3633',
  '3634',
  '3635',
  '3636',
  '3637',
  '3638',
  '3640',
  '3641',
  '3645',
  '3650',
  '3660',
  '3661',
  '3670',
  '3671'
];

export const VATSchemeTypeCodeReceivingESPSII = {
  '1': {
    code: '01',
    desc: 'General system transaction.'
  },
  '2': {
    code: '02',
    desc: 'Transactions for which business persons pay compensation for purchasing from individuals under the Special system for agricultural, livestock and fisheries activities.'
  },
  '3': {
    code: '03',
    desc: 'Transactions to which the special system of used goods, works of art, antiquities and collectables applies.'
  },
  '4': {
    code: '04',
    desc: 'Special regime for gold investment.'
  },
  '5': {
    code: '05',
    desc: 'Special system for travel agencies.'
  },
  '6': {
    code: '06',
    desc: 'Special system applicable to groups of entities, VAT (Advanced)'
  },
  '7': {
    code: '07',
    desc: 'Special cash basis system.'
  },
  '8': {
    code: '08',
    desc: 'Transactions subject to the IPSI / IGIC (Tax on Production, Services and Imports / General Indirect Canary Islands Tax)'
  },
  '9': {
    code: '09',
    desc: 'Intra-Community acquisitions of goods and services.'
  },
  '12': {
    code: '12',
    desc: 'Business premises lease activities.'
  },
  '13': {
    code: '13',
    desc: 'Invoice corresponding to an import (reported without being associated with a DUA)'
  },
  '14': {
    code: '14',
    desc: 'First half 2017 and other invoices prior to entry in the SII.'
  }
};

export const VATSchemeTypeCodeSendingESPSII = {
  '1': {
    code: '01',
    desc: 'General system transaction.'
  },
  '2': {
    code: '02',
    desc: 'Export.'
  },
  '3': {
    code: '03',
    desc: 'Transactions to which the special system of used goods, works of art, antiquities and collectables applies.'
  },
  '4': {
    code: '04',
    desc: 'Special regime for gold investment.'
  },
  '5': {
    code: '05',
    desc: 'Special system for travel agencies.'
  },
  '6': {
    code: '06',
    desc: 'Special system applicable to groups of entities, VAT (Advanced)'
  },
  '7': {
    code: '07',
    desc: 'Special cash basis system.'
  },
  '8': {
    code: '08',
    desc: 'Transactions subject to IPSI / IGIC (Tax on Production, Services and Imports / Canary Islands General Indirect Tax).'
  },
  '9': {
    code: '09',
    desc: 'Invoicing of the provision of travel agency services acting as intermediaries in the name of and on behalf of other persons (Additional Provision 4, RD1619/2012)'
  },
  '10': {
    code: '10',

    desc: 'Collection on behalf of third parties of professional fees or industrial property, copyright or other such rights by partners, associates or members undertaken by companies, associates, professional organisations or othe entities that undertaken these collections.'
  },
  '11': {
    code: '11',
    desc: 'Business premises lease activities subject to withholding'
  },
  '12': {
    code: '12',
    desc: 'Business premises lease activities not subject to withholding.'
  },
  '13': {
    code: '13',
    desc: 'Business premises lease activities subject and not subject to withholding.'
  },
  '14': {
    code: '14',
    desc: 'Invoice with VAT pending accrual on work certifications with Public Administration recipients.'
  },
  '15': {
    code: '15',
    desc: 'Invoice with VAT pending accrual on transactions of a consecutive nature.'
  },
  '16': {
    code: '16',
    desc: 'First half 2017 and other invoices prior to entry in the Sil.'
  },
  '17': {
    code: '17',
    desc: 'Transaction covered by one of the schemes provided for in Chapter XI of Title IX (OSS and IOSS)'
  }
};

/**
 * Removes common prefixes from an ID string.
 * @param id The ID string to sanitize.
 * @returns The sanitized ID string.
 */
export const sanitizeId = (id: string): string | undefined => {
  const prefixes = ['user_', 'usr_', 'org_', 'inv_', 'crd_', 'dst_'];

  // Handle null and undefined values
  if (!id) {
    return;
  }

  for (const prefix of prefixes) {
    if (id.startsWith(prefix)) {
      return id.slice(prefix.length);
    }
  }
  return id;
};

export const prefixId = (
  id: string,
  type: 'org' | 'organisation' | 'inv' | 'invoice' | 'crd' | 'creditNote' | 'user' | 'usr'
): string => {
  switch (type) {
    case 'org':
    case 'organisation':
      return `org_${sanitizeId(id)}`;
    case 'inv':
    case 'invoice':
      return `inv_${sanitizeId(id)}`;
    case 'crd':
    case 'creditNote':
      return `crd_${sanitizeId(id)}`;
    case 'usr':
    case 'user':
      return `usr_${sanitizeId(id)}`;
  }
};

export type JobItem = {
  id: string;
  organisationId: string;
  status: string;
  created: string;
  modified: string;
  filename: string;
  size: number;
  meta: {
    started: String;
    lines: number;
    errors: {
      total: number;
    };
    skipped: {
      total: number;
    };
    success: {
      total: number;
    };
  };
};

export const SampleCSV = [
  // invoice
  {
    group: 'invoice',
    label: 'Generic',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20generic.csv'
  },
  {
    group: 'invoice',
    label: 'China',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20chn.csv'
  },
  {
    group: 'invoice',
    label: 'Denmark',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20dnk%20nemhandel.csv'
  },
  {
    group: 'invoice',
    label: 'France',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20fra%20-%20ubl.csv'
  },
  {
    group: 'invoice',
    label: 'Germany - Peppol',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20deu%20-%20peppol.csv'
  },
  {
    group: 'invoice',
    label: 'Germany - XRechnung',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20deu%20-%20xrechnung.csv'
  },
  {
    group: 'invoice',
    label: 'Germany - ZUGFerd',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20deu%20-%20zugferd.csv'
  },
  {
    group: 'invoice',
    label: 'Greece',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20grc.csv'
  },
  {
    group: 'invoice',
    label: 'Hungary',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20hun.csv'
  },
  {
    group: 'invoice',
    label: 'India',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20ind.csv'
  },
  {
    group: 'invoice',
    label: 'Italy',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20ita.csv'
  },
  // {
  //   group: 'invoice',
  //   label: 'Jordan',
  //   value: ''
  // },
  {
    group: 'invoice',
    label: 'Japan',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20jpn.csv'
  },
  {
    group: 'invoice',
    label: 'Malaysia',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20mys.csv'
  },
  {
    group: 'invoice',
    label: 'Mexico',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20mex.csv'
  },
  {
    group: 'invoice',
    label: 'Peppol',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20peppol.csv'
  },
  {
    group: 'invoice',
    label: 'Portugal',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20prt.csv'
  },
  {
    group: 'invoice',
    label: 'Poland',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20pol.csv'
  },
  {
    group: 'invoice',
    label: 'Romania',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20rou.csv'
  },
  {
    group: 'invoice',
    label: 'Saudi Arabia',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20sau.csv'
  },
  {
    group: 'invoice',
    label: 'Spain SII',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20esp%20sii.csv'
  },
  {
    group: 'invoice',
    label: 'Spain Verifactu',
    value: 'https://dev.eezi.io/csv-files/invoice%20-%20esp%20verifactu.csv'
  },
  // Credit Notes
  {
    group: 'credit-note',
    label: 'Generic',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20generic.csv'
  },
  // {
  //   group: 'credit-note',
  //   label: 'China',
  //   value: ''
  // },
  {
    group: 'credit-note',
    label: 'Denmark',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20dnk%20nemhandel.csv'
  },
  {
    group: 'credit-note',
    label: 'Germany',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20deu.csv'
  },
  {
    group: 'credit-note',
    label: 'Greece',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20grc.csv'
  },
  // {
  //   group: 'credit-note',
  //   label: 'India',
  //   value: ''
  // },
  {
    group: 'credit-note',
    label: 'Italy',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20ita.csv'
  },
  // {
  //   group: 'credit-note',
  //   label: 'Jordan',
  //   value: ''
  // },
  // {
  //   group: 'credit-note',
  //   label: 'Japan',
  //   value: ''
  // },
  {
    group: 'credit-note',
    label: 'Malaysia',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20mys.csv'
  },
  {
    group: 'credit-note',
    label: 'Mexico',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20mex.csv'
  },
  // {
  //   group: 'credit-note',
  //   label: 'Peppol',
  //   value: ''
  // },
  {
    group: 'credit-note',
    label: 'Poland',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20pol.csv'
  },
  {
    group: 'credit-note',
    label: 'Portugal',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20prt.csv'
  },
  {
    group: 'credit-note',
    label: 'Romania',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20rou.csv'
  },
  {
    group: 'credit-note',
    label: 'Saudi Arabia',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20sau.csv'
  },
  {
    group: 'credit-note',
    label: 'Spain SII',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20esp%20sii.csv'
  },
  {
    group: 'credit-note',
    label: 'Spain Verifactu',
    value: 'https://dev.eezi.io/csv-files/credit%20note%20-%20esp%20verifactu.csv'
  }
];
